// ProjectManagerInterface/src/pages/ClientDetails.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, Title } from 'chart.js';
import { Pie, Bar } from 'react-chartjs-2';

ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, Title);

import {
    Container, Box, Typography, Button, Paper, Grid, CircularProgress,
    TextField, FormControl, InputLabel, Select, MenuItem,
    Avatar, Chip, IconButton, List, ListItem, ListItemIcon, ListItemText, Divider,
    Tabs, Tab, Dialog, DialogTitle, DialogContent, DialogActions,
    Card, CardContent, CardActions, Autocomplete, Rating
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import BusinessIcon from '@mui/icons-material/Business';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import AssignmentIcon from '@mui/icons-material/Assignment';
import FlagIcon from '@mui/icons-material/Flag';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import LanguageIcon from '@mui/icons-material/Language';
import FilePresentIcon from '@mui/icons-material/FilePresent';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import DownloadIcon from '@mui/icons-material/Download';
import VisibilityIcon from '@mui/icons-material/Visibility';
import InfoIcon from '@mui/icons-material/Info';
import FolderIcon from '@mui/icons-material/Folder';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import EventNoteIcon from '@mui/icons-material/EventNote';
import DateRangeIcon from '@mui/icons-material/DateRange';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ReceiptIcon from '@mui/icons-material/Receipt';
import ContactsIcon from '@mui/icons-material/Contacts';
import BarChartIcon from '@mui/icons-material/BarChart';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import PersonIcon from '@mui/icons-material/Person';
import API_URL from '../config';

function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ py: 3, px: { xs: 1, sm: 2, md: 3 }, overflowX: 'hidden' }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const ClientDetails = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const [client, setClient] = useState(null);
    const [loading, setLoading] = useState(true);
    const [editMode, setEditMode] = useState(false);
    const [updateLoading, setUpdateLoading] = useState(false);
    const [originalAccountType, setOriginalAccountType] = useState(null);
    const [editFiles, setEditFiles] = useState({ license_1: null, license_2: null, tax_card: null, establishment_card: null, application_pdf: null });

    // Initial Approval/Rejection Action State
    const [confirmAction, setConfirmAction] = useState({ open: false, type: null, loading: false, rejectReason: '' });

    const [tabValue, setTabValue] = useState(0);
    const [countries, setCountries] = useState([]);
    const [divisions, setDivisions] = useState([]);

    // Project Creation from Quote State
    const [openCreateProject, setOpenCreateProject] = useState(false);
    const [creatingProject, setCreatingProject] = useState(false);
    const [createProjectData, setCreateProjectData] = useState({
        name: '', quote_ref: '', start_date: '', due_date: '',
        country_id: '', division_id: '', client_id: '', description: '',
        priority: 'Medium'
    });

    // File Preview State
    const [previewFile, setPreviewFile] = useState(null);
    const [previewLoading, setPreviewLoading] = useState(true);

    // Followup states
    const [clientFollowups, setClientFollowups] = useState([]);
    const [users, setUsers] = useState([]);
    const [leads, setLeads] = useState([]);
    const [newFollowupDateTime, setNewFollowupDateTime] = useState('');
    const [newFollowupDetails, setNewFollowupDetails] = useState('');
    const [newFollowupNextVisit, setNewFollowupNextVisit] = useState('');
    const [newFollowupAssignedUser, setNewFollowupAssignedUser] = useState('');
    const [newFollowupProject, setNewFollowupProject] = useState('');
    const [newFollowupLead, setNewFollowupLead] = useState('');
    const [editingFollowupId, setEditingFollowupId] = useState(null);
    const [followupDialogOpen, setFollowupDialogOpen] = useState(false);
    const [followupLoading, setFollowupLoading] = useState(false);

    const [newFollowup, setNewFollowup] = useState({
        startDate: '',
        startTime: '',
        endDate: '',
        endTime: '',
        nextVisitStartDate: '',
        nextVisitStartTime: '',
        nextVisitEndDate: '',
        nextVisitEndTime: '',
        details: '',
        assigned_to_id: '',
        project_id: '',
        lead_crm_id: ''
    });

    const [quotes, setQuotes] = useState([]);
    const [loadingQuotes, setLoadingQuotes] = useState(false);
    const [quotesFetched, setQuotesFetched] = useState(false);

    const [contacts, setContacts] = useState([]);
    const [loadingContacts, setLoadingContacts] = useState(false);
    const [contactsFetched, setContactsFetched] = useState(false);
    const [openContactDialog, setOpenContactDialog] = useState(false);
    const [newContact, setNewContact] = useState({
        name: '',
        last_name: '',
        salutation: '',
        department: '',
        profession_level: '',
        decision_maker: '',
        job_title: '',
        email: '',
        phone: ''
    });

    // Fallback or read userRole
    const userRole = localStorage.getItem('user_role') || 'Member';
    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };

    useEffect(() => {
        fetchClient();
        fetchCountries();
        fetchDivisions();
        fetchFollowups();
        fetchUsers();
        fetchLeads();
    }, [id]);

    const fetchClient = async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${API_URL}/clients/${id}`, { headers });
            setClient(res.data);
            setOriginalAccountType(res.data.account_type);
        } catch (err) {
            console.error(err);
            alert("Error fetching client details");
        } finally {
            setLoading(false);
        }
    };

    const fetchCountries = async () => {
        try {
            const res = await axios.get(`${API_URL}/setup/countries`, { headers });
            setCountries(res.data);
        } catch (err) {
            console.error(err);
        }
    };

    const fetchDivisions = async () => {
        try {
            const res = await axios.get(`${API_URL}/setup/divisions`, { headers });
            setDivisions(res.data);
        } catch (err) {
            console.error(err);
        }
    };

    const fetchFollowups = async () => {
        try {
            const res = await axios.get(`${API_URL}/clients/${id}/followups`, { headers });
            setClientFollowups(res.data);
        } catch (err) { console.error(err); }
    };
    const fetchUsers = async () => {
        try {
            const res = await axios.get(`${API_URL}/setup/users`, { headers });
            setUsers(res.data);
        } catch (err) { console.error(err); }
    };
    const fetchLeads = async () => {
        try {
            const res = await axios.get(`${API_URL}/leads`, { headers });
            setLeads(res.data);
        } catch (err) { console.error(err); }
    };

    const fetchQuotes = async () => {
        if (!client || !client.crmID) return;
        setLoadingQuotes(true);
        try {
            const res = await axios.get(`${API_URL}/clients/${id}/quotes`, { headers });
            if (res.data.success) {
                setQuotes(res.data.quotes);
            } else {
                setQuotes([]);
            }
        } catch (err) { 
            console.error('Error fetching quotes:', err);
            setQuotes([]);
        } finally {
            setLoadingQuotes(false);
            setQuotesFetched(true);
        }
    };

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const actionParam = params.get('action');

        if (actionParam && client && !confirmAction.open && !confirmAction.type) {
            if (actionParam === 'approve' && client.status !== 'Approved') {
                setConfirmAction({ open: true, type: 'approve', loading: false, rejectReason: '' });
            } else if (actionParam === 'reject' && client.status !== 'Rejected') {
                setConfirmAction({ open: true, type: 'reject', loading: false, rejectReason: '' });
            }
        }
    }, [location.search, client]);

    const handleConfirmSubmit = async () => {
        const { type, rejectReason } = confirmAction;
        setConfirmAction(prev => ({ ...prev, loading: true }));
        try {
            if (type === 'approve') {
                await axios.put(`${API_URL}/clients/${id}`, { status: 'Approved' }, { headers });
                // Removed alert for better UX
            } else if (type === 'reject') {
                await axios.put(`${API_URL}/clients/${id}`, { status: 'Rejected', rejection_comment: rejectReason }, { headers });
                // Removed alert for better UX
            }
            await fetchClient();
            // Clear URL params
            navigate(`/clients/${id}`, { replace: true });
        } catch (err) {
            console.error(err);
            alert(`Failed to ${type} client.`);
        } finally {
            setConfirmAction({ open: false, type: null, loading: false, rejectReason: '' });
        }
    };

    const fetchContacts = async () => {
        setLoadingContacts(true);
        try {
            const res = await axios.get(`${API_URL}/clients/${id}/contacts`, { headers });
            if (res.data.success) {
                setContacts(res.data.contacts);
            } else {
                setContacts([]);
            }
        } catch (err) {
            console.error('Error fetching contacts:', err);
            setContacts([]);
        } finally {
            setLoadingContacts(false);
            setContactsFetched(true);
        }
    };

    const handleAddContact = async () => {
        if (!newContact.name) return;
        setLoadingContacts(true);
        try {
            const res = await axios.post(`${API_URL}/clients/${id}/contacts`, newContact, { headers });
            if (res.data.success) {
                setContacts([...contacts, res.data.contact]);
                setOpenContactDialog(false);
                setNewContact({ name: '', last_name: '', salutation: '', department: '', profession_level: '', decision_maker: '', job_title: '', email: '', phone: '' });
                alert('Contact saved successfully' + (client.crmID ? ' to Database and Dynamics 365' : ''));
            } else {
                alert('Failed to add contact');
            }
        } catch (err) {
            console.error(err);
            alert('Error saving contact');
        } finally {
            setLoadingContacts(false);
        }
    };

    useEffect(() => {
        if (client?.crmID && (tabValue === 5 || tabValue === 6) && !quotesFetched) {
            fetchQuotes();
        }
        if (tabValue === 1 && !contactsFetched) {
            fetchContacts();
        }
    }, [tabValue, quotesFetched, contactsFetched, client]);

    const handleAddFollowup = async () => {
        if (!newFollowupDetails) return;
        setFollowupLoading(true);
        try {
            const startStr = newFollowup.startDate && newFollowup.startTime ? `${newFollowup.startDate}T${newFollowup.startTime}` : null;
            const endStr = newFollowup.endDate && newFollowup.endTime ? `${newFollowup.endDate}T${newFollowup.endTime}` : null;
            const nextVisitStartStr = newFollowup.nextVisitStartDate && newFollowup.nextVisitStartTime ? `${newFollowup.nextVisitStartDate}T${newFollowup.nextVisitStartTime}` : null;
            const nextVisitEndStr = newFollowup.nextVisitEndDate && newFollowup.nextVisitEndTime ? `${newFollowup.nextVisitEndDate}T${newFollowup.nextVisitEndTime}` : null;
            
            const payload = {
                date_time: startStr,
                date_time_end: endStr,
                next_visit: nextVisitStartStr,
                next_visit_end: nextVisitEndStr,
                details: newFollowupDetails,
                assigned_to_id: newFollowupAssignedUser || null,
                project_id: newFollowupProject || null,
                lead_crm_id: newFollowupLead || null
            };
            await axios.post(`${API_URL}/clients/${id}/followups`, payload, { headers });
            setNewFollowupDateTime('');
            setNewFollowupDetails('');
            setNewFollowupNextVisit('');
            setNewFollowupAssignedUser('');
            setNewFollowupProject('');
            setNewFollowupLead('');
            fetchFollowups();
        } catch (err) {
            console.error(err);
        } finally {
            setFollowupLoading(false);
        }
    };

    const handleOpenFollowupDialog = (followup = null) => {
        if (followup) {
            setEditingFollowupId(followup.id);
            setNewFollowup({
                startDate: followup.date_time ? followup.date_time.split('T')[0] : '',
                startTime: followup.date_time ? followup.date_time.split('T')[1]?.slice(0, 5) : '',
                endDate: followup.date_time_end ? followup.date_time_end.split('T')[0] : '',
                endTime: followup.date_time_end ? followup.date_time_end.split('T')[1]?.slice(0, 5) : '',
                nextVisitStartDate: followup.next_visit ? followup.next_visit.split('T')[0] : '',
                nextVisitStartTime: followup.next_visit ? followup.next_visit.split('T')[1]?.slice(0, 5) : '',
                nextVisitEndDate: followup.next_visit_end ? followup.next_visit_end.split('T')[0] : '',
                nextVisitEndTime: followup.next_visit_end ? followup.next_visit_end.split('T')[1]?.slice(0, 5) : '',
                details: followup.details || '',
                assigned_to_id: followup.assigned_to_id || '',
                project_id: followup.project_id || '',
                lead_crm_id: followup.lead_crm_id || ''
            });
        } else {
            setEditingFollowupId(null);
            setNewFollowup({
                startDate: '',
                startTime: '',
                endDate: '',
                endTime: '',
                nextVisitStartDate: '',
                nextVisitStartTime: '',
                nextVisitEndDate: '',
                nextVisitEndTime: '',
                details: '',
                assigned_to_id: '',
                project_id: '',
                lead_crm_id: ''
            });
        }
        setFollowupDialogOpen(true);
    };

    const handleDeleteFollowup = async (followupId) => {
        if (!window.confirm("Are you sure you want to delete this follow-up?")) return;
        try {
            await axios.delete(`${API_URL}/clients/${id}/followups/${followupId}`, { headers });
            fetchFollowups();
        } catch (err) {
            console.error('Error deleting follow-up:', err);
        }
    };

    const handleFollowupSubmit = async (e) => {
        e.preventDefault();
        try {
            const startStr = newFollowup.startDate && newFollowup.startTime ? `${newFollowup.startDate}T${newFollowup.startTime}` : null;
            const endStr = newFollowup.endDate && newFollowup.endTime ? `${newFollowup.endDate}T${newFollowup.endTime}` : null;
            const nextVisitStartStr = newFollowup.nextVisitStartDate && newFollowup.nextVisitStartTime ? `${newFollowup.nextVisitStartDate}T${newFollowup.nextVisitStartTime}` : null;
            const nextVisitEndStr = newFollowup.nextVisitEndDate && newFollowup.nextVisitEndTime ? `${newFollowup.nextVisitEndDate}T${newFollowup.nextVisitEndTime}` : null;
            
            const payload = {
                date_time: startStr,
                date_time_end: endStr,
                next_visit: nextVisitStartStr,
                next_visit_end: nextVisitEndStr,
                details: newFollowup.details,
                assigned_to_id: newFollowup.assigned_to_id || null,
                project_id: newFollowup.project_id || null,
                lead_crm_id: newFollowup.lead_crm_id || null
            };
            if (editingFollowupId) {
                await axios.put(`${API_URL}/clients/${id}/followups/${editingFollowupId}`, payload, { headers });
            } else {
                await axios.post(`${API_URL}/clients/${id}/followups`, payload, { headers });
            }
            setFollowupDialogOpen(false);
            fetchFollowups();
        } catch (err) {
            console.error('Error adding follow-up:', err);
        }
    };

    const handleDeleteClient = async () => {
        if(!window.confirm("Are you sure you want to delete this client?")) return;
        try {
            await axios.delete(`${API_URL}/clients/${id}`, { headers });        
            alert("Client deleted successfully");
            navigate('/clients');
        } catch (err) {
            console.error(err);
            alert(err.response?.data?.message || "Error deleting client");      
        }
    };

    const handleUpdateClient = async () => {
        setUpdateLoading(true);
        const upgradingToCRM = originalAccountType === 'PM' && client.account_type === 'CRM';
        if (upgradingToCRM) {
            const allDocs = editFiles.license_1 && editFiles.license_2 && editFiles.tax_card && editFiles.establishment_card && editFiles.application_pdf;
            if (!allDocs) {
                alert('Please upload all required documents to upgrade this account to CRM.');
                setUpdateLoading(false);
                return;
            }
        }
        try {
            if (upgradingToCRM) {
                const formData = new FormData();
                const payload = { ...client, country_id: client.country_id || null, status: 'Pending', approved: false };
                Object.entries(payload).forEach(([k, v]) => { if (v !== null && v !== undefined && typeof v !== 'object') formData.append(k, v); });
                if (client.country_id) formData.append('country_id', client.country_id);
                Object.entries(editFiles).forEach(([k, v]) => { if (v) formData.append(k, v); });
                await axios.put(`${API_URL}/clients/${id}`, formData, { headers: { ...headers, 'Content-Type': 'multipart/form-data' } });
            } else {
                const payload = { ...client, country_id: client.country_id || null };
                await axios.put(`${API_URL}/clients/${id}`, payload, { headers });
            }
            setEditMode(false);
            setEditFiles({ license_1: null, license_2: null, tax_card: null, establishment_card: null, application_pdf: null });
            fetchClient();
        } catch (err) {
            console.error('Error updating client:', err.response?.data || err);
            alert('Error updating client: ' + (err.response?.data?.error || err.response?.data?.message || err.message));
        } finally {
            setUpdateLoading(false);
        }
    };

    const handleDownloadDocument = async (filename) => {
        const encodedFilename = encodeURIComponent(filename);
        const url = `${API_URL}/clients/${id}/files/${encodedFilename}`;
        
        // Use fetch to trigger a proper download if it's rendered inline
        try {
            const response = await fetch(url, { headers });
            const blob = await response.blob();
            const downloadUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.download = filename.split('/').pop() || 'document';
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(downloadUrl);
        } catch (error) {
            console.error('Error downloading file:', error);
            // Fallback to opening in new tab
            window.open(url, '_blank');
        }
    };

    const handlePreviewDocument = (filename) => {
        const encodedFilename = encodeURIComponent(filename);
        const url = `${API_URL}/clients/${id}/files/${encodedFilename}`;
        setPreviewLoading(true);
        setPreviewFile({ filename, url });
    };

    const handleOpenCreateProject = (quote) => {
        const quoteCountryStr = (quote.country || '').toLowerCase();
        // Priority match against exact "country" field (e.g. from CRM), 
        // fall back to a "pm country" field if the user specifically requested it, though it's standard 'country' in API
        const pmCountryStr = (quote.pm_country || quoteCountryStr).toLowerCase();
        
        const matchingCountry = countries.find(c => c.name.toLowerCase() === pmCountryStr);
        const matchingDivision = divisions.find(d => d.name.toLowerCase() === (quote.division || '').toLowerCase());

        setCreateProjectData({
            name: quote.project_name || '',
            // Prioritize main_quote_ref over ref_id
            quote_ref: quote.main_quote_ref || quote.ref_id || '',
            start_date: quote.starts ? quote.starts.split('T')[0] : '',
            due_date: '',
            client_id: id,
            country_id: matchingCountry ? matchingCountry.id : '',
            division_id: matchingDivision ? matchingDivision.id : '',
            description: `Auto-generated from quote ${quote.main_quote_ref || quote.ref_id || ''}`,
            priority: 'Medium'
        });
        setOpenCreateProject(true);
    };
    const handleOpenCreateBlankProject = () => {
        setCreateProjectData({
            name: '',
            quote_ref: 'N/A', 
            start_date: '',
            due_date: '',
            client_id: id,
            country_id: client.country_id || '',
            division_id: '',
            description: '',
            priority: 'Medium'
        });
        setOpenCreateProject(true);
    };

    const handleCreateProject = async () => {
        try {
            if (!createProjectData.name || !createProjectData.country_id || !createProjectData.division_id) {
                alert("Please fill in the project name, country, and division.");
                return;
            }
            setCreatingProject(true);
            await axios.post(`${API_URL}/projects`, createProjectData, { headers });
            alert("Project created successfully!");
            setOpenCreateProject(false);
            fetchClient();
            setTabValue(4); // Navigate to Projects tab
        } catch (err) {
            console.error(err);
            alert("Failed to create project");
        } finally {
            setCreatingProject(false);
        }
    };

    if (loading || !client) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
    <Box sx={{ width: '100%', overflowX: 'hidden', boxSizing: 'border-box', pb: 6, px: { xs: 1, md: 3 }, bgcolor: '#f4f6f8', minHeight: '100vh' }}>
      
      {/* Modern Gradient Header */}
      <Paper elevation={0} sx={{ 
          mb: 4, 
          mt: 2, 
          p: 4, 
          borderRadius: 4, 
          background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)', 
          color: 'white',
          boxShadow: '0 10px 30px rgba(30, 60, 114, 0.2)',
          position: 'relative',
          overflow: 'hidden'
      }}>
        <Box sx={{ position: 'absolute', top: -50, right: -50, width: 200, height: 200, borderRadius: '50%', background: 'rgba(255,255,255,0.05)' }} />
        <Box sx={{ position: 'absolute', bottom: -100, right: 100, width: 250, height: 250, borderRadius: '50%', background: 'rgba(255,255,255,0.05)' }} />

        <Box sx={{ position: 'relative', zIndex: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: { xs: 'flex-start', md: 'flex-start' }, justifyContent: 'space-between', gap: 3 }}> 
            <Box sx={{ flex: 1 }}>
              <Button
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate('/clients')}
                sx={{ color: '#90caf9', mb: 2, pl: 0, fontWeight: 'bold', '&:hover': { color: 'white', bgcolor: 'transparent', transform: 'translateX(-4px)' }, transition: 'all 0.2s' }}
              >
                Back to Clients Focus
              </Button>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5 }}>
                <Avatar sx={{ 
                    width: 52, height: 52, flexShrink: 0,
                    bgcolor: 'rgba(255,255,255,0.15)', 
                    color: '#e3f2fd', 
                    boxShadow: '0 6px 18px rgba(0,0,0,0.2)',
                    border: '2px solid rgba(255,255,255,0.2)',
                    fontSize: '1.3rem', fontWeight: 700
                }}>
                  {client.name[0]?.toUpperCase() || <BusinessIcon />}
                </Avatar>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Typography variant="h5" fontWeight="900" sx={{ letterSpacing: 0.3, wordBreak: 'break-word', color: 'white', lineHeight: 1.3 }}>
                    {client.name}
                  </Typography>
                  {client.status === 'Approved' && <CheckCircleIcon sx={{ color: '#4caf50', fontSize: 22, flexShrink: 0 }} />}
                </Box>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', ml: 0.5 }}>
                <Chip 
                    icon={<BusinessIcon sx={{ fontSize: '1rem !important' }} />} 
                    label={`CRM ID: ${client.crmID || 'N/A'}`} 
                    size="small" 
                    sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: 'white', fontWeight: 700, borderRadius: 1.5 }} 
                />
                {client.crmGUID && (
                    <Chip 
                        icon={<InfoIcon sx={{ fontSize: '1rem !important' }} />} 
                        label={`GUID: ${client.crmGUID}`} 
                        size="small" 
                        sx={{ bgcolor: 'rgba(255,255,255,0.1)', color: 'white', fontWeight: 600, borderRadius: 1.5 }} 
                    />
                )}
              </Box>
            </Box>
            
            <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' }, width: { xs: '100%', md: 'auto' }, pt: { xs: 2, md: 0 } }}>
                <Button
                    variant="contained"
                    startIcon={editMode ? <CloseIcon /> : <EditIcon />}
                    onClick={() => setEditMode(!editMode)}
                    sx={{
                        borderRadius: 2,
                        bgcolor: editMode ? 'rgba(244,67,54,0.9)' : 'white',
                        color: editMode ? 'white' : '#1e3c72',
                        fontWeight: 'bold',
                        px: 3,
                        boxShadow: '0 4px 14px rgba(0,0,0,0.2)',
                        '&:hover': {
                            bgcolor: editMode ? '#d32f2f' : '#f0f0f0',
                            transform: 'translateY(-2px)'
                        },
                        transition: 'all 0.2s'
                    }}
                >
                    {editMode ? 'Cancel Edit' : 'Edit Details'}
                </Button>
            </Box>
            </Box>
          </Box>
      </Paper>

      {/* Content Area */}
      <Paper elevation={0} sx={{ borderRadius: 4, overflow: 'hidden', boxShadow: '0 4px 20px rgba(0,0,0,0.04)', minHeight: '400px', bgcolor: 'white', border: '1px solid #f0f0f0' }}>
        <Tabs
          value={tabValue}
          onChange={(e, newValue) => setTabValue(newValue)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            px: 2,
            pt: 2,
            borderBottom: '1px solid #f1f5f9',
            '& .MuiTab-root': {
              minHeight: 64,
              textTransform: 'none',
              fontWeight: 700,
              fontSize: '0.95rem',
              color: '#64748b',
              mr: 2,
              '&.Mui-selected': {
                color: '#1976d2',
              }
            },
            '& .MuiTabs-indicator': {
              height: 3,
              borderTopLeftRadius: 3,
              borderTopRightRadius: 3,
              backgroundColor: '#1976d2'
            }
          }}
        >
            <Tab value={0} icon={<InfoIcon />} iconPosition="start" label="Overview" />
            <Tab value={1} icon={<ContactsIcon />} iconPosition="start" label="Contacts" />
            <Tab value={2} icon={<FolderIcon />} iconPosition="start" label="Documents" />
            <Tab value={3} icon={<EventNoteIcon />} iconPosition="start" label="Follow-ups" />
            <Tab value={4} icon={<AccountTreeIcon />} iconPosition="start" label="Projects" />
            {client?.crmID && <Tab value={5} icon={<ReceiptIcon />} iconPosition="start" label="Quotes" />}
            <Tab value={6} icon={<BarChartIcon />} iconPosition="start" label="Statistics" />
        </Tabs>

        <TabPanel value={tabValue} index={0}>
                    {editMode ? (
                        <Box sx={{ mt: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                                <Avatar sx={{ bgcolor: 'rgba(25, 118, 210, 0.1)', color: 'primary.main', width: 44, height: 44 }}>
                                    <PersonIcon />
                                </Avatar>
                                <Typography variant="h5" fontWeight="800" color="primary.main">
                                    Edit Client Profile
                                </Typography>
                            </Box>

                            <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: 3, border: '1px solid #e2e8f0', bgcolor: '#ffffff' }}>
                                <Typography variant="subtitle2" sx={{ mb: 3, fontWeight: 800, color: '#64748b', textTransform: 'uppercase' }}>Basic Information</Typography>
                                <Grid container spacing={3}>
                                    <Grid item xs={12} md={6}>
                                        <TextField label="Name" fullWidth value={client.name} onChange={e => setClient({...client, name: e.target.value})} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
                                    </Grid>
                                    <Grid item xs={12} md={3}>
                                        <TextField label="CRM ID" fullWidth value={client.crmID || ''} onChange={e => setClient({...client, crmID: e.target.value})} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
                                    </Grid>
                                    <Grid item xs={12} md={3}>
                                        <TextField label="CRM GUID" fullWidth value={client.crmGUID || ''} onChange={e => setClient({...client, crmGUID: e.target.value})} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <TextField label="Email" fullWidth value={client.email} onChange={e => setClient({...client, email: e.target.value})} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />        
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <TextField label="Phone" fullWidth value={client.phone} onChange={e => setClient({...client, phone: e.target.value})} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />        
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <FormControl fullWidth sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}>
                                            <InputLabel>Country</InputLabel>
                                            <Select
                                                value={client.country_id || ''}
                                                label="Country"
                                                onChange={e => setClient({...client, country_id: e.target.value})}
                                            >
                                                {countries.map((c) => (
                                                    <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                </Grid>
                            </Paper>

                            <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: 3, border: '1px solid #e2e8f0', bgcolor: '#ffffff' }}>
                                <Typography variant="subtitle2" sx={{ mb: 3, fontWeight: 800, color: '#64748b', textTransform: 'uppercase' }}>Classification & Ranking</Typography>
                                <Grid container spacing={3}>
                                    <Grid item xs={12} sm={6}>
                                        <FormControl fullWidth sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}>
                                            <InputLabel>Client Type</InputLabel>
                                            <Select
                                                value={client.client_type || 'Contractor'}
                                                label="Client Type"
                                                onChange={e => setClient({...client, client_type: e.target.value})}
                                            >
                                                <MenuItem value="Contractor">Contractor</MenuItem>
                                                <MenuItem value="Stake Holder">Stake Holder</MenuItem>
                                                <MenuItem value="Consultant">Consultant</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <FormControl fullWidth sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}>
                                            <InputLabel>Account Type</InputLabel>
                                            <Select
                                                value={client.account_type || 'CRM'}
                                                label="Account Type"
                                                onChange={e => {
                                                    if ((client.crm_id || client.crm_guid) && e.target.value === 'PM') {
                                                        alert("Cannot change back to PM because CRM ID is already filled or approved.");
                                                        return;
                                                    }
                                                    if (e.target.value === 'CRM') {
                                                        setEditFiles({ license_1: null, license_2: null, tax_card: null, establishment_card: null, application_pdf: null });
                                                    }
                                                    setClient({...client, account_type: e.target.value});
                                                }}
                                            >
                                                <MenuItem value="CRM">CRM</MenuItem>
                                                <MenuItem value="PM">PM</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    {editMode && originalAccountType === 'PM' && client.account_type === 'CRM' && (
                                        <Grid item xs={12}>
                                            <Paper elevation={0} sx={{ p: 2, border: '1px solid #fbbf24', borderRadius: 2, bgcolor: '#fffbeb' }}>
                                                <Typography variant="subtitle2" fontWeight="800" color="#b45309" mb={1}>Upload Required Documents to sync to CRM</Typography>
                                                <Typography variant="body2" color="#92400e" mb={2}>All 5 documents are required. After saving, this account will be sent for approval.</Typography>
                                                <Grid container spacing={2}>
                                                    {['license_1', 'license_2', 'tax_card', 'establishment_card', 'application_pdf'].map(docKey => (
                                                        <Grid item xs={12} sm={6} md={4} key={docKey}>
                                                            <Typography variant="caption" fontWeight="700" color="#475569" display="block" mb={0.5}>{docKey.replace(/_/g, ' ').toUpperCase()} {editFiles[docKey] ? '✓' : '*'}</Typography>
                                                            <Button variant="outlined" component="label" size="small" fullWidth
                                                                sx={{ borderRadius: 2, borderColor: editFiles[docKey] ? '#22c55e' : '#fbbf24', color: editFiles[docKey] ? '#16a34a' : '#92400e', fontWeight: 'bold', justifyContent: 'flex-start', textTransform: 'none', overflow: 'hidden', whiteSpace: 'nowrap' }}>
                                                                {editFiles[docKey] ? editFiles[docKey].name : 'Choose file...'}
                                                                <input type="file" hidden onChange={e => setEditFiles(prev => ({ ...prev, [docKey]: e.target.files[0] || null }))} />
                                                            </Button>
                                                        </Grid>
                                                    ))}
                                                </Grid>
                                            </Paper>
                                        </Grid>
                                    )}
                                    <Grid item xs={12} sm={4}>
                                        <FormControl fullWidth>
                                            <Box sx={{ border: '1px solid #cbd5e1', borderRadius: 2, p: '4.5px 14px', minHeight: 56, display: 'flex', flexDirection: 'column', position: 'relative', '&:hover': { borderColor: '#334155' }, '&:focus-within': { borderColor: '#1976d2', borderWidth: 2 } }}>
                                                <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.75rem', position: 'absolute', top: -9, left: 10, bgcolor: 'white', px: 0.5, zIndex: 1 }}>Internal Rank</Typography>
                                                <Rating
                                                    value={Number(client.rank) || 1}
                                                    max={3}
                                                    sx={{ mt: 1.5, color: '#fbbf24' }}
                                                    onChange={(event, newValue) => setClient({...client, rank: newValue ? newValue.toString() : '1'})}
                                                />
                                            </Box>
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <FormControl fullWidth>
                                            <Box sx={{ border: '1px solid #cbd5e1', borderRadius: 2, p: '4.5px 14px', minHeight: 56, display: 'flex', flexDirection: 'column', position: 'relative', '&:hover': { borderColor: '#334155' }, '&:focus-within': { borderColor: '#1976d2', borderWidth: 2 } }}>
                                                <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.75rem', position: 'absolute', top: -9, left: 10, bgcolor: 'white', px: 0.5, zIndex: 1 }}>External Rank</Typography>
                                                <Rating
                                                    value={Number(client.external_rank) || 1}
                                                    max={3}
                                                    sx={{ mt: 1.5, color: '#fbbf24' }}
                                                    onChange={(event, newValue) => setClient({...client, external_rank: newValue ? newValue.toString() : '1'})}
                                                />
                                            </Box>
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <TextField
                                            label="VO Rank"
                                            fullWidth
                                            type="number"
                                            value={client.ventures_onsite_rank || ''}
                                            onChange={(e) => setClient({...client, ventures_onsite_rank: e.target.value})}
                                            inputProps={{ min: 0 }}
                                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                        />
                                    </Grid>
                                </Grid>
                            </Paper>

                            <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: 3, border: '1px solid #e2e8f0', bgcolor: '#ffffff' }}>
                                <Typography variant="subtitle2" sx={{ mb: 3, fontWeight: 800, color: '#64748b', textTransform: 'uppercase' }}>Financials & Additional Info</Typography>
                                <Grid container spacing={3}>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            label="Cash"
                                            fullWidth
                                            value={client.cash || ''}
                                            onChange={e => setClient({...client, cash: e.target.value})}
                                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            label="Credit"
                                            fullWidth
                                            value={client.credit || ''}
                                            onChange={e => setClient({...client, credit: e.target.value})}
                                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField
                                            label="Contact Info / Additional Notes"
                                            fullWidth multiline rows={3}
                                            value={client.contact_info || ''}
                                            onChange={e => setClient({...client, contact_info: e.target.value})}
                                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                        />
                                    </Grid>
                                </Grid>
                            </Paper>

                            {['Admin', 'Manager', 'Country Manager', 'Division Manager'].includes(userRole) && (
                                <Paper elevation={0} sx={{ p: 4, bgcolor: '#fff8e1', border: '1px solid #ffe082', borderRadius: 3, mt: 2, mb: 3 }}>
                                    <Typography variant="h6" fontWeight="bold" color="#ff8f00" mb={1} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <AssignmentIcon /> Administrative Access: Approval Processing
                                    </Typography>
                                    <Divider sx={{ mb: 3, borderColor: 'rgba(255,143,0,0.2)' }} />
                                    
                                    <Grid container spacing={3}>
                                        <Grid item xs={12} sm={6}>
                                            <FormControl fullWidth sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: 'white' } }}>  
                                                <InputLabel>Approval Status</InputLabel>
                                                <Select
                                                    value={client.status || (client.approved ? 'Approved' : 'Pending')}
                                                    label="Approval Status"
                                                    onChange={(e) => setClient({...client, status: e.target.value, approved: e.target.value === 'Approved'})}
                                                >
                                                    <MenuItem value="Pending">Pending</MenuItem>
                                                    <MenuItem value="Approved">Approved</MenuItem>
                                                    <MenuItem value="Rejected">Rejected</MenuItem>
                                                    <MenuItem value="Not registered">Not registered</MenuItem>
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <FormControl fullWidth sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: 'white' } }}>
                                                <InputLabel>Enable Approved Flag</InputLabel>
                                                <Select
                                                    value={client.approved ? 'true' : 'false'}
                                                    label="Enable Approved Flag"    
                                                    onChange={(e) => setClient({...client, approved: e.target.value === 'true'})}
                                                >
                                                    <MenuItem value="false">No (False)</MenuItem>
                                                    <MenuItem value="true">Yes (True)</MenuItem>
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                        {client.status === 'Rejected' && (      
                                            <Grid item xs={12}>
                                                <TextField
                                                    label="Rejection Reason"        
                                                    fullWidth
                                                    multiline
                                                    rows={3}
                                                    value={client.rejection_comment || ''}
                                                    onChange={e => setClient({...client, rejection_comment: e.target.value})}
                                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: 'white' } }}
                                                />
                                            </Grid>
                                        )}
                                    </Grid>
                                </Paper>
                            )}
                        </Box>
                    ) : (
                        <Box sx={{ mt: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                    <Avatar sx={{ bgcolor: 'rgba(71, 85, 105, 0.1)', color: '#475569', width: 44, height: 44 }}>
                                        <BusinessIcon />
                                    </Avatar>
                                    <Typography variant="h5" fontWeight="800" color="#334155">Client Profile</Typography>
                                </Box>
                                {['Admin', 'Manager', 'Country Manager', 'Division Manager'].includes(userRole) && client.status === 'Approved' && (
                                    <Box sx={{ bgcolor: '#dcfce7', display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1, borderRadius: 2, border: '1px solid #bbf7d0' }}>
                                        <CheckCircleIcon sx={{ color: '#16a34a', fontSize: 20 }} />
                                        <Typography variant="body2" fontWeight="700" color="#166534">Verified Account</Typography>
                                    </Box>
                                )}
                            </Box>

                            <Grid container spacing={3}>
                                <Grid item xs={12} md={8}>
                                    <Card elevation={0} sx={{ mb: 3, borderRadius: 4, boxShadow: '0 4px 20px rgba(0,0,0,0.03)', border: '1px solid #f1f5f9', bgcolor: '#ffffff' }}>
                                <CardContent sx={{ p: 4 }}>
                                    <Typography variant="h6" fontWeight="800" mb={3} color="#1e293b" sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                        <PersonIcon sx={{ color: '#3b82f6' }} /> Contact Information & Location
                                    </Typography>
                                    <Grid container spacing={3}>
                                        <Grid item xs={12} sm={6}>
                                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2.5 }}>
                                                <Avatar sx={{ bgcolor: '#eff6ff', color: '#2563eb', width: 44, height: 44, boxShadow: '0 2px 10px rgba(37,99,235,0.15)' }}>
                                                    <EmailIcon />
                                                </Avatar>
                                                <Box>
                                                    <Typography variant="caption" color="#64748b" fontWeight="700" textTransform="uppercase" letterSpacing={0.5}>Email Address</Typography>
                                                    <Typography variant="body1" fontWeight="600" color="#1e293b" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        {client.email || '—'}
                                                        {client.email && <Chip size="small" label="Primary" sx={{ height: 20, fontSize: '0.65rem', bgcolor: '#e0e7ff', color: '#4338ca', fontWeight: 700 }} />}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2.5 }}>
                                                <Avatar sx={{ bgcolor: '#f0fdf4', color: '#16a34a', width: 44, height: 44, boxShadow: '0 2px 10px rgba(22,163,74,0.15)' }}>
                                                    <PhoneIcon />
                                                </Avatar>
                                                <Box>
                                                    <Typography variant="caption" color="#64748b" fontWeight="700" textTransform="uppercase" letterSpacing={0.5}>Phone Number</Typography>
                                                    <Typography variant="body1" fontWeight="600" color="#1e293b">{client.phone || '—'}</Typography>
                                                </Box>
                                            </Box>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2.5 }}>
                                                    <Avatar sx={{ bgcolor: '#fef2f2', color: '#ef4444', width: 44, height: 44, boxShadow: '0 2px 10px rgba(239,68,68,0.15)' }}>
                                                        <LanguageIcon />
                                                    </Avatar>
                                                    <Box>
                                                        <Typography variant="caption" color="#64748b" fontWeight="700" textTransform="uppercase" letterSpacing={0.5}>Country Location</Typography>
                                                        <Typography variant="body1" fontWeight="600" color="#1e293b">{countries.find(c => c.id === client.country_id)?.name || '—'}</Typography>
                                                    </Box>
                                                </Box>
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2.5 }}>
                                                    <Avatar sx={{ bgcolor: '#fffbeb', color: '#f59e0b', width: 44, height: 44, boxShadow: '0 2px 10px rgba(245,158,11,0.15)' }}>
                                                        <ContactsIcon />
                                                    </Avatar>
                                                    <Box>
                                                        <Typography variant="caption" color="#64748b" fontWeight="700" textTransform="uppercase" letterSpacing={0.5}>Additional Contacts</Typography>
                                                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', color: '#475569', fontWeight: 500 }}>{client.contact_info || '—'}</Typography>
                                                    </Box>
                                                </Box>
                                            </Grid>
                                        </Grid>
                                    </CardContent>
                                </Card>

                                <Card elevation={0} sx={{ borderRadius: 4, boxShadow: '0 4px 20px rgba(0,0,0,0.03)', border: '1px solid #f1f5f9', bgcolor: '#ffffff' }}>
                                    <CardContent sx={{ p: 4 }}>
                                        <Typography variant="h6" fontWeight="800" mb={3} color="#1e293b" sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                            <AssignmentIcon sx={{ color: '#8b5cf6' }} /> Account Classification
                                        </Typography>
                                        <Grid container spacing={3} alignItems="stretch">
                                            <Grid item xs={12} sm={3}>
                                                <Box sx={{ height: '100%', textAlign: 'center', p: 2.5, bgcolor: "#f8fafc", borderRadius: 3, border: '1px solid #e2e8f0', transition: 'all 0.2s', '&:hover': { borderColor: '#cbd5e1', bgcolor: '#f1f5f9' }, display: 'flex', flexDirection: 'column' }}>
                                                    <Typography variant="caption" color="#64748b" fontWeight="bold" textTransform="uppercase" letterSpacing={1}>Client Type</Typography>
                                                    <Box sx={{ mt: 'auto', display: 'flex', justifyContent: 'center', minHeight: 24, alignItems: 'center' }}>
                                                        <Typography variant="h6" fontWeight="800" color="#334155">{client.client_type || '—'}</Typography>
                                                    </Box>
                                                </Box>
                                            </Grid>
                                            <Grid item xs={12} sm={3}>
                                                <Box sx={{ height: '100%', textAlign: 'center', p: 2.5, bgcolor: "#f8fafc", borderRadius: 3, border: '1px solid #e2e8f0', transition: 'all 0.2s', '&:hover': { borderColor: '#cbd5e1', bgcolor: '#f1f5f9' }, display: 'flex', flexDirection: 'column' }}>
                                                    <Typography variant="caption" color="#64748b" fontWeight="bold" textTransform="uppercase" letterSpacing={1}>Account Platform</Typography>
                                                    <Box sx={{ mt: 'auto', display: 'flex', justifyContent: 'center', minHeight: 24, alignItems: 'center' }}>
                                                        <Typography variant="h6" fontWeight="800" color={client.account_type === 'PM' ? '#9c27b0' : '#0369a1'}>
                                                            {client.account_type || '—'}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </Grid>
                                            <Grid item xs={12} sm={2}>
                                                <Box sx={{ height: '100%', textAlign: 'center', p: 2.5, bgcolor: "#f8fafc", borderRadius: 3, border: '1px solid #e2e8f0', transition: 'all 0.2s', '&:hover': { borderColor: '#cbd5e1', bgcolor: '#f1f5f9' }, display: 'flex', flexDirection: 'column' }}>
                                                    <Typography variant="caption" color="#64748b" fontWeight="bold" textTransform="uppercase" letterSpacing={1}>Rank</Typography>
                                                    <Box sx={{ mt: 'auto', display: 'flex', justifyContent: 'center', minHeight: 24, alignItems: 'center' }}>
                                                        <Rating value={parseInt(client.rank, 10) || 0} max={3} readOnly size="small" sx={{ color: '#fbbf24' }} />
                                                    </Box>
                                                </Box>
                                            </Grid>
                                            <Grid item xs={12} sm={2}>
                                                <Box sx={{ height: '100%', textAlign: 'center', p: 2.5, bgcolor: "#f8fafc", borderRadius: 3, border: '1px solid #e2e8f0', transition: 'all 0.2s', '&:hover': { borderColor: '#cbd5e1', bgcolor: '#f1f5f9' }, display: 'flex', flexDirection: 'column' }}>
                                                    <Typography variant="caption" color="#64748b" fontWeight="bold" textTransform="uppercase" letterSpacing={1}>Ext Rank</Typography>
                                                    <Box sx={{ mt: 'auto', display: 'flex', justifyContent: 'center', minHeight: 24, alignItems: 'center' }}>
                                                        <Rating value={parseInt(client.external_rank, 10) || 0} max={3} readOnly size="small" sx={{ color: '#fbbf24' }} />
                                                    </Box>
                                                </Box>
                                            </Grid>
                                            <Grid item xs={12} sm={2}>
                                                <Box sx={{ height: '100%', textAlign: 'center', p: 2.5, bgcolor: "#f8fafc", borderRadius: 3, border: '1px solid #e2e8f0', transition: 'all 0.2s', '&:hover': { borderColor: '#cbd5e1', bgcolor: '#f1f5f9' }, display: 'flex', flexDirection: 'column' }}>
                                                    <Typography variant="caption" color="#64748b" fontWeight="bold" textTransform="uppercase" letterSpacing={1}>ERank</Typography>
                                                    <Box sx={{ mt: 'auto', display: 'flex', justifyContent: 'center', minHeight: 24, alignItems: 'center' }}>
                                                        <Typography variant="h6" fontWeight="800" color="#334155">{client.ventures_onsite_rank || '—'}</Typography>
                                                    </Box>
                                                </Box>
                                            </Grid>
                                        </Grid>
                                    </CardContent>
                                </Card>
                            </Grid>
                            
                            <Grid item xs={12} md={4}>
                                <Card elevation={0} sx={{ borderRadius: 4, mb: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.03)', border: '1px solid #f1f5f9', bgcolor: '#ffffff' }}>
                                    <CardContent sx={{ p: 4 }}>
                                        <Typography variant="h6" fontWeight="800" mb={3} color="#1e293b" sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                            <AssignmentIcon sx={{ color: '#ff9800' }} /> Status & Approval
                                        </Typography>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2, mb: 3 }}>
                                            {client.status === 'Approved' ? <CheckCircleIcon sx={{ color: '#4caf50', fontSize: 48, filter: 'drop-shadow(0 2px 4px rgba(76,175,80,0.3))' }} /> :
                                             client.status === 'Rejected' ? <CancelIcon sx={{ color: '#f44336', fontSize: 48, filter: 'drop-shadow(0 2px 4px rgba(244,67,54,0.3))' }} /> :
                                             client.status === 'Not registered' ? <CancelIcon sx={{ color: '#9e9e9e', fontSize: 48 }} /> :
                                             <AssignmentIcon sx={{ color: '#ff9800', fontSize: 48, filter: 'drop-shadow(0 2px 4px rgba(255,152,0,0.3))' }} />}
                                            <Box>
                                                <Typography variant="h4" fontWeight="900" color="#1e293b" sx={{ letterSpacing: -0.5 }}>
                                                    {client.status || (client.approved ? "Approved" : "Pending")}
                                                </Typography>
                                            </Box>
                                        </Box>
                                        {client.status === 'Rejected' && client.rejection_comment && (
                                            <Box sx={{ mt: 3, p: 2.5, bgcolor: '#fef2f2', borderRadius: 3, borderLeft: '4px solid #ef4444' }}>
                                                <Typography variant="caption" fontWeight="800" color="#b91c1c" textTransform="uppercase" letterSpacing={0.5}>Rejection Reason</Typography>
                                                <Typography variant="body2" color="#991b1b" sx={{ mt: 1, fontWeight: 500, lineHeight: 1.6 }}>
                                                    {client.rejection_comment}
                                                </Typography>
                                            </Box>
                                        )}
                                    </CardContent>
                                </Card>
                                
                                <Card elevation={0} sx={{ borderRadius: 4, mb: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.03)', border: '1px solid #f1f5f9', bgcolor: '#ffffff' }}>
                                    <CardContent sx={{ p: 4 }}>
                                        <Typography variant="h6" fontWeight="800" mb={3} color="#1e293b" sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                            <AssignmentIcon sx={{ color: '#4caf50' }} /> Payment
                                        </Typography>
                                        <Grid container spacing={2}>
                                            <Grid item xs={6}>
                                                <Box sx={{ textAlign: 'center', p: 2.5, bgcolor: "#f8fafc", borderRadius: 3, border: '1px solid #e2e8f0', transition: 'all 0.2s', '&:hover': { borderColor: '#cbd5e1', bgcolor: '#f1f5f9' } }}>
                                                    <Typography variant="caption" color="#64748b" fontWeight="bold" textTransform="uppercase" letterSpacing={1}>Cash</Typography>
                                                    <Typography variant="h6" fontWeight="800" color="#334155" mt={1}>{client.cash || '—'}</Typography>
                                                </Box>
                                            </Grid>
                                            <Grid item xs={6}>
                                                <Box sx={{ textAlign: 'center', p: 2.5, bgcolor: "#f8fafc", borderRadius: 3, border: '1px solid #e2e8f0', transition: 'all 0.2s', '&:hover': { borderColor: '#cbd5e1', bgcolor: '#f1f5f9' } }}>
                                                    <Typography variant="caption" color="#64748b" fontWeight="bold" textTransform="uppercase" letterSpacing={1}>Credit</Typography>
                                                    <Typography variant="h6" fontWeight="800" color="#334155" mt={1}>{client.credit || '—'}</Typography>
                                                </Box>
                                            </Grid>
                                        </Grid>
                                    </CardContent>
                                </Card>
                            </Grid>
                        </Grid>
                    </Box>
                )}
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
            <Box sx={{ mt: 1, p: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
                    <Typography variant="h5" fontWeight="800" color="#1e293b">Client Contacts</Typography>
                    <Button 
                        variant="contained" 
                        startIcon={<ContactsIcon />} 
                        onClick={() => setOpenContactDialog(true)}
                        sx={{ 
                            borderRadius: 2, 
                            fontWeight: 'bold', 
                            px: 3, 
                            boxShadow: '0 4px 14px rgba(25,118,210,0.3)',
                            background: 'linear-gradient(45deg, #1976d2 0%, #42a5f5 100%)',
                            '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 6px 20px rgba(25,118,210,0.4)', background: 'linear-gradient(45deg, #1565c0 0%, #1e88e5 100%)' }
                        }}
                    >
                        Add Contact
                    </Button>
                </Box>
                {loadingContacts ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}><CircularProgress size={48} thickness={4} /></Box>
                ) : contacts.length === 0 ? (
                    <Paper elevation={0} sx={{ p: 6, textAlign: 'center', border: '2px dashed #e2e8f0', borderRadius: 4, bgcolor: "#f8fafc" }}>
                        <ContactsIcon sx={{ fontSize: 64, color: '#cbd5e1', mb: 2 }} />
                        <Typography variant="h6" color="#64748b" fontWeight="700" mb={1}>No contacts found</Typography>
                        <Typography variant="body2" color="#94a3b8">Add contacts to keep track of the client's key personnel.</Typography>
                    </Paper>
                ) : (
                    <Grid container spacing={3}>
                        {contacts.map((contact, idx) => (
                            <Grid item xs={12} sm={6} md={4} key={contact.id || idx}>
                                <Card elevation={0} sx={{ 
                                    borderRadius: 4, 
                                    boxShadow: '0 4px 20px rgba(0,0,0,0.03)', 
                                    border: '1px solid #f1f5f9', 
                                    height: '100%',
                                    transition: 'all 0.3s ease',
                                    '&:hover': { transform: 'translateY(-5px)', boxShadow: '0 12px 28px rgba(0,0,0,0.06)', borderColor: '#cbd5e1' }
                                }}>
                                    <CardContent sx={{ p: 3 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, mb: 2.5 }}>
                                            <Avatar sx={{ 
                                                bgcolor: '#eff6ff', 
                                                color: '#3b82f6', 
                                                width: 56, 
                                                height: 56, 
                                                fontSize: '1.5rem', 
                                                fontWeight: 'bold',
                                                boxShadow: '0 4px 10px rgba(59,130,246,0.15)'
                                            }}>
                                                {contact.name ? contact.name[0].toUpperCase() : <ContactsIcon />}
                                            </Avatar>
                                            <Box>
                                                <Typography variant="h6" fontWeight="800" color="#1e293b" lineHeight={1.2} sx={{ mb: 0.5 }}>
                                                    {contact.salutation} {contact.name} {contact.last_name}
                                                </Typography>
                                                <Chip 
                                                    label={contact.job_title || 'No Job Title'} 
                                                    size="small" 
                                                    sx={{ bgcolor: '#f1f5f9', color: '#475569', fontWeight: 600, borderRadius: 1.5 }} 
                                                />
                                            </Box>
                                        </Box>
                                        <Divider sx={{ my: 2, borderColor: '#f1f5f9' }} />
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                <Box sx={{ p: 1, borderRadius: 2, bgcolor: "#f8fafc", color: '#64748b', display: 'flex' }}>
                                                    <EmailIcon fontSize="small" />
                                                </Box>
                                                <Typography variant="body2" fontWeight="500" color="#334155" sx={{ wordBreak: 'break-all' }}>
                                                    {contact.email || '—'}
                                                </Typography>
                                            </Box>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                <Box sx={{ p: 1, borderRadius: 2, bgcolor: "#f8fafc", color: '#64748b', display: 'flex' }}>
                                                    <PhoneIcon fontSize="small" />
                                                </Box>
                                                <Typography variant="body2" fontWeight="500" color="#334155">
                                                    {contact.phone || '—'}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid>
                          ))}
                    </Grid>
                )}
            </Box>
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
            <Box sx={{ mt: 1, p: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
                    <Typography variant="h5" fontWeight="800" color="#1e293b">Attached Documents</Typography>
                </Box>
                <Grid container spacing={3}>
                    {['license_1', 'license_2', 'tax_card', 'establishment_card', 'application_pdf'].map((docKey) => {
                        const fileValue = client.files ? client.files[docKey] : client[docKey];
                        if (fileValue) {
                            return (
                                <Grid item xs={12} sm={6} md={4} key={docKey}>
                                    <Card
                                        elevation={0}
                                        sx={{
                                            borderRadius: 4,
                                            display: 'flex',
                                            flexDirection: 'column',
                                            height: '100%',
                                            transition: 'all 0.3s ease',
                                            border: '1px solid #f1f5f9',
                                            boxShadow: '0 4px 20px rgba(0,0,0,0.03)',
                                            '&:hover': { 
                                                transform: 'translateY(-6px)', 
                                                boxShadow: '0 12px 28px rgba(0,0,0,0.08)', 
                                                borderColor: '#3b82f6' 
                                            }
                                        }}
                                    >
                                      <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', pt: 5, pb: 3 }}>
                                        <Box sx={{ 
                                            p: 2, 
                                            borderRadius: '50%', 
                                            bgcolor: '#eff6ff', 
                                            mb: 3,
                                            boxShadow: 'inset 0 2px 4px rgba(59,130,246,0.1)'
                                        }}>
                                            <InsertDriveFileIcon sx={{ fontSize: 40, color: '#2563eb' }} />
                                        </Box>
                                        <Typography variant="subtitle1" fontWeight="800" color="#1e293b" noWrap sx={{ width: '100%', mb: 1.5 }} title={docKey.replace('_', ' ').toUpperCase()}>
                                            {docKey.replace('_', ' ').toUpperCase()}
                                        </Typography>
                                        <Chip label="Document File" size="small" sx={{ bgcolor: "#f8fafc", color: '#64748b', fontWeight: 700, borderRadius: 1.5, border: '1px solid #e2e8f0' }} />
                                      </CardContent>
                                      <CardActions sx={{ justifyContent: 'space-between', p: 2.5, bgcolor: '#ffffff', borderTop: '1px solid #f1f5f9', gap: 1 }}>
                                          <Button 
                                              size="small" 
                                              variant="outlined"
                                              startIcon={<VisibilityIcon />}
                                              onClick={() => handlePreviewDocument(fileValue)}
                                              sx={{ flexBasis: '50%', borderRadius: 2, fontWeight: 'bold', borderColor: '#cbd5e1', color: '#475569', '&:hover': { bgcolor: "#f8fafc", borderColor: '#94a3b8' } }}
                                          >
                                              Preview
                                          </Button>
                                          <Button 
                                              size="small" 
                                              variant="contained"
                                              startIcon={<DownloadIcon />}
                                              onClick={() => handleDownloadDocument(fileValue)}
                                              sx={{ 
                                                flexBasis: '50%', borderRadius: 2, fontWeight: 'bold', 
                                                boxShadow: 'none', background: '#ebf5ff', color: '#2563eb', 
                                                '&:hover': { background: '#2563eb', color: 'white', boxShadow: '0 4px 12px rgba(37,99,235,0.2)' } 
                                              }}
                                          >
                                              Download
                                          </Button>
                                      </CardActions>
                                    </Card>
                                </Grid>
                            );
                        }
                        return null;
                    })}
                    {(!client.files || (!client.files.license_1 && !client.files.license_2 && !client.files.tax_card && !client.files.establishment_card && !client.files.application_pdf)) && 
                    (!client.license_1 && !client.license_2 && !client.tax_card && !client.establishment_card && !client.application_pdf) && (
                        <Grid item xs={12}>
                            <Paper elevation={0} sx={{ p: 6, textAlign: 'center', border: '2px dashed #e2e8f0', borderRadius: 4, bgcolor: "#f8fafc" }}>
                                <FolderIcon sx={{ fontSize: 64, color: '#cbd5e1', mb: 2 }} />
                                <Typography variant="h6" color="#64748b" fontWeight="700" mb={1}>No Documents Uploaded</Typography>
                                <Typography variant="body2" color="#94a3b8">There are currently no files attached to this client profile.</Typography>
                            </Paper>
                        </Grid>
                    )}
                </Grid>
            </Box>
        </TabPanel>

        <TabPanel value={tabValue} index={4}>
            <Box sx={{ mt: 1, p: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4, flexWrap: 'wrap', gap: 2 }}>
                    <Typography variant="h5" fontWeight="800" color="#1e293b">Client Projects</Typography>
                    <Button 
                        variant="contained" 
                        startIcon={<AddIcon />} 
                        onClick={handleOpenCreateBlankProject}
                        sx={{ 
                            borderRadius: 2, 
                            fontWeight: 'bold', 
                            px: 3, 
                            boxShadow: '0 4px 14px rgba(25,118,210,0.3)',
                            background: 'linear-gradient(45deg, #1976d2 0%, #42a5f5 100%)',
                            '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 6px 20px rgba(25,118,210,0.4)', background: 'linear-gradient(45deg, #1565c0 0%, #1e88e5 100%)' }
                        }}
                    >
                        New Project
                    </Button>
                </Box>
                <Grid container spacing={3}>
                    {client.projects && client.projects.map(project => (        
                        <Grid item xs={12} sm={6} md={4} key={project.id}>      
                            <Card elevation={0} sx={{ 
                                height: '100%', display: 'flex', flexDirection: 'column', 
                                border: '1px solid #f1f5f9', borderRadius: 4, 
                                transition: 'transform 0.2s', 
                                '&:hover': { transform: 'translateY(-4px)', borderColor: '#cbd5e1', boxShadow: '0 10px 25px rgba(0,0,0,0.05)' },
                                cursor: 'pointer'
                            }} onClick={() => navigate(`/projects/${project.id}`)}>
                                <Box sx={{ p: 2.5, bgcolor: "#f8fafc", borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                        <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: '#eff6ff', color: '#3b82f6', border: '1px solid #dbeafe' }}>
                                            <AccountTreeIcon sx={{ fontSize: 24 }} />
                                        </Box>
                                        <Typography variant="subtitle1" fontWeight="800" color="#1e293b" sx={{ wordBreak: 'break-word', lineHeight: 1.3 }}>
                                            {project.name}
                                        </Typography>
                                    </Box>
                                </Box>
                                <CardContent sx={{ flexGrow: 1, py: 2.5, px: 3 }}>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12}>
                                            <Typography variant="caption" color="#94a3b8" fontWeight="600" display="block" textTransform="uppercase" letterSpacing={0.5} mb={0.5}>Project Status</Typography>
                                            <Chip 
                                                label={project.status || "Planned"} 
                                                size="small" 
                                                sx={{ 
                                                    height: 22, fontSize: '0.7rem', fontWeight: 700, 
                                                    bgcolor: project.status === 'Completed' ? '#f0fdf4' : project.status === 'In Progress' ? '#fffbeb' : '#eff6ff',
                                                    color: project.status === 'Completed' ? '#16a34a' : project.status === 'In Progress' ? '#d97706' : '#1d4ed8'
                                                }} 
                                            />
                                        </Grid>
                                        {project.ref && (
                                            <Grid item xs={12}>
                                                <Divider sx={{ my: 1, borderColor: '#f1f5f9' }} />
                                                <Box sx={{ mt: 1, p: 1.5, bgcolor: "#f8fafc", borderRadius: 2, border: '1px dashed #cbd5e1' }}>
                                                    <Typography variant="caption" color="#64748b" fontWeight="600" display="block">Project Ref</Typography>
                                                    <Typography variant="body2" color="#475569" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>{project.ref}</Typography>
                                                </Box>
                                            </Grid>
                                        )}
                                    </Grid>
                                </CardContent>
                                <CardActions sx={{ borderTop: '1px solid #f1f5f9', bgcolor: '#ffffff', p: 2, pt: 1.5 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
                                        <Button 
                                            size="small" 
                                            endIcon={<ArrowBackIcon sx={{ transform: 'rotate(180deg)' }}/>} 
                                            sx={{ fontWeight: '800', color: '#3b82f6', '&:hover': { background: 'transparent', textDecoration: 'underline' } }}
                                            onClick={(e) => { e.stopPropagation(); navigate(`/projects/${project.id}`); }}
                                        >
                                            View Details
                                        </Button>
                                    </Box>
                                </CardActions>
                            </Card>
                        </Grid>
                      ))}
                    {(!client.projects || client.projects.length === 0) && (    
                        <Grid item xs={12}>
                            <Paper elevation={0} sx={{ p: 6, textAlign: 'center', border: '2px dashed #e2e8f0', borderRadius: 4, bgcolor: "#f8fafc" }}>       
                                <AccountTreeIcon sx={{ fontSize: 64, color: '#cbd5e1', mb: 2 }} />
                                <Typography variant="h6" color="#64748b" fontWeight="700" mb={1}>No Projects Yet</Typography>
                                <Typography variant="body2" color="#94a3b8" mb={3}>This client doesn't have any projects associated with them.</Typography>
                                <Button 
                                    variant="outlined" 
                                    startIcon={<AddIcon />} 
                                    onClick={handleOpenCreateBlankProject}
                                    sx={{ borderRadius: 2, fontWeight: 'bold', borderColor: '#cbd5e1', color: '#475569', '&:hover': { bgcolor: 'white', borderColor: '#94a3b8' } }}
                                >
                                    Create First Project
                                </Button>
                            </Paper>
                        </Grid>
                    )}
                </Grid>
            </Box>
        </TabPanel>

        <TabPanel value={tabValue} index={3}>
            <Box sx={{ mt: 1, p: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4, flexWrap: 'wrap', gap: 2 }}>
                    <Typography variant="h5" fontWeight="800" color="#1e293b">Client Follow-Ups</Typography>
                    <Button 
                        variant="contained" 
                        startIcon={<AddIcon />}
                        onClick={() => handleOpenFollowupDialog()} 
                        sx={{ 
                            borderRadius: 2, 
                            fontWeight: 'bold', 
                            px: 3, 
                            boxShadow: '0 4px 14px rgba(25,118,210,0.3)',
                            background: 'linear-gradient(45deg, #1976d2 0%, #42a5f5 100%)',
                            '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 6px 20px rgba(25,118,210,0.4)', background: 'linear-gradient(45deg, #1565c0 0%, #1e88e5 100%)' }
                        }}
                    >
                        Log Follow-up
                    </Button>
                </Box>

            <Box>
                {clientFollowups.length === 0 ? (
                    <Paper elevation={0} sx={{ p: 6, textAlign: 'center', border: '2px dashed #e2e8f0', borderRadius: 4, bgcolor: "#f8fafc" }}>
                        <EventNoteIcon sx={{ fontSize: 64, color: '#cbd5e1', mb: 2 }} />
                        <Typography variant="h6" color="#64748b" fontWeight="700" mb={1}>No follow-ups recorded</Typography>
                        <Typography variant="body2" color="#94a3b8">Log your interactions and upcoming activities with this client.</Typography>
                    </Paper>
                ) : (
                    <Box sx={{ position: 'relative', pl: { xs: 2.5, md: 5 }, pr: { xs: 1, md: 3 }, '&::before': { content: '""', position: 'absolute', top: 0, bottom: 0, left: { xs: '10px', md: '20px' }, width: '3px', bgcolor: '#e2e8f0', borderRadius: 3, zIndex: 0 }, display: 'flex', flexDirection: 'column', gap: 4 }}>
                        {clientFollowups.map(f => {
    const isAuto = f.details && f.details.startsWith("[Next Visit]");
    return (
        <Box key={f.id} sx={{ position: 'relative', zIndex: 1, mb: 1 }}>
            {/* Timeline Dot */}
            <Box sx={{ position: 'absolute', left: { xs: -24, md: -45 }, top: 24, width: 18, height: 18, borderRadius: '50%', background: isAuto ? 'linear-gradient(135deg, #a855f7 0%, #7c3aed 100%)' : 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)', border: '4px solid #ffffff', boxShadow: '0 0 0 2px #e2e8f0' }} />
            
            <Card elevation={0} sx={{ border: '1px solid', borderColor: isAuto ? '#ede9fe' : '#e2e8f0', borderRadius: 4, transition: 'all 0.3s ease', boxShadow: '0 4px 20px rgba(0,0,0,0.03)', '&:hover': { borderColor: isAuto ? '#c4b5fd' : '#cbd5e1', boxShadow: '0 10px 30px rgba(0,0,0,0.06)', transform: 'translateY(-3px)' }, overflow: 'hidden' }}>
                {/* Header Profile */}
                <Box sx={{ p: 2.5, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, borderBottom: '1px solid', borderColor: isAuto ? '#ede9fe' : '#f1f5f9', background: isAuto ? 'linear-gradient(135deg, #f5f3ff 0%, #ede9fe 100%)' : 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)', gap: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar sx={{ bgcolor: isAuto ? '#8b5cf6' : '#2563eb', color: 'white', width: 44, height: 44, boxShadow: '0 4px 10px rgba(0,0,0,0.1)' }}>
                            {isAuto ? <AccessTimeIcon /> : <EventNoteIcon />}
                        </Avatar>
                        <Box>
                            <Typography variant="subtitle1" fontWeight="800" color="#1e293b">
                                {f.date_time && isAuto ? f.details.replace('[Next Visit] ', '') : (f.date_time ? new Date(f.date_time).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) : 'No Start Date')} 
                                {f.date_time_end && ` — ${new Date(f.date_time_end).toLocaleString(undefined, { timeStyle: 'short' })}`}
                            </Typography>
                            <Box sx={{ mt: 0.5, display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                                {isAuto ? (
                                    <Chip label="Auto-Generated" size="small" sx={{ height: 20, fontSize: '0.65rem', fontWeight: 800, bgcolor: '#e0e7ff', color: '#4f46e5' }} />
                                ) : null}
                                {f.assigned_to_name && (
                                    <Typography variant="caption" color="#64748b" fontWeight="600">
                                        Assignee: <span style={{ color: '#334155' }}>{f.assigned_to_name}</span>
                                    </Typography>
                                )}
                                <Typography variant="caption" color="#94a3b8" fontWeight="500">
                                    • Logged by: {f.created_by_name || 'System'}
                                </Typography>
                            </Box>
                        </Box>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button size="small" variant="outlined" startIcon={<EditIcon />} onClick={() => handleOpenFollowupDialog(f)} sx={{ borderRadius: 2, fontWeight: 'bold', borderColor: '#cbd5e1', bgcolor: 'white', color: '#475569', '&:hover': { bgcolor: '#f8fafc' } }}>
                            Edit
                        </Button>
                        {['Admin', 'Manager'].includes(userRole) && (<Button size="small" variant="outlined" color="error" onClick={() => handleDeleteFollowup(f.id)} sx={{ minWidth: 0, px: 1, borderRadius: 2, borderColor: '#fecaca', bgcolor: 'white', '&:hover': { bgcolor: '#fef2f2' } }}><DeleteIcon fontSize="small" /></Button>)}
                    </Box>
                </Box>

                {/* Body Content */}
                <CardContent sx={{ p: 3, bgcolor: 'white' }}>
                    <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', color: '#334155', lineHeight: 1.6, fontSize: '1rem', mb: 3 }}>
                        {f.details ? (!isAuto ? f.details : f.details.replace('[Next Visit] ', '')) : 'No additional details provided.'}
                    </Typography>

                    {/* Metadata Footer */}
                    { (f.next_visit || f.next_visit_end || f.project_name || f.lead_crm_id) && (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, pt: 2.5, borderTop: '1px dashed #e2e8f0' }}>
                            {f.next_visit && (
                                <Chip 
                                    icon={<DateRangeIcon fontSize="small" sx={{ color: '#d97706 !important' }} />}
                                    label={`Start: ${new Date(f.next_visit).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' })}`}
                                    sx={{ bgcolor: '#fffbeb', color: '#d97706', fontWeight: 600, borderRadius: 1.5, px: 1, border: '1px solid #fef3c7' }} 
                                />
                            )}
                            {f.next_visit_end && (
                                <Chip 
                                    icon={<AccessTimeIcon fontSize="small" sx={{ color: '#d97706 !important' }} />}
                                    label={`End: ${new Date(f.next_visit_end).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' })}`}
                                    sx={{ bgcolor: '#fffbeb', color: '#d97706', fontWeight: 600, borderRadius: 1.5, px: 1, border: '1px solid #fef3c7' }} 
                                />
                            )}
                            {(f.next_visit || f.next_visit_end) && (
                                <Chip 
                                    icon={<AccessTimeIcon fontSize="small" sx={{ color: '#7c3aed !important' }} />}
                                    label={`Next Visit: ${f.next_visit ? new Date(f.next_visit).toLocaleDateString() : ''} ${f.next_visit_end ? '- ' + new Date(f.next_visit_end).toLocaleDateString() : ''}`}
                                    sx={{ bgcolor: '#f5f3ff', color: '#6d28d9', fontWeight: 700, borderRadius: 1.5, px: 1, border: '1px solid #ede9fe' }} 
                                />
                            )}
                            {f.project_name && (
                                <Chip 
                                    icon={<AssignmentIcon fontSize="small" sx={{ color: '#0369a1 !important' }} />}
                                    label={`Project: ${f.project_name}`}
                                    sx={{ bgcolor: '#f0f9ff', color: '#0369a1', fontWeight: 600, borderRadius: 1.5, px: 1, border: '1px solid #e0f2fe' }} 
                                />
                            )}
                            {f.lead_crm_id && (
                                <Chip 
                                    icon={<BusinessIcon fontSize="small" sx={{ color: '#0f766e !important' }} />}
                                    label={`CRM Lead Ref: ${f.lead_crm_id}`}
                                    sx={{ bgcolor: '#f0fdfa', color: '#0f766e', fontWeight: 600, borderRadius: 1.5, px: 1, border: '1px solid #ccfbf1' }} 
                                />
                            )}
                        </Box>
                    )}
                </CardContent>
            </Card>
        </Box>
    );
})}


                    </Box>
                )}
            </Box>
            </Box>
        </TabPanel>

        {client?.crmID && (
          <TabPanel value={tabValue} index={5}>
            <Box sx={{ mt: 1, p: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4, flexWrap: 'wrap', gap: 2 }}>
                    <Typography variant="h5" fontWeight="800" color="#1e293b">CRM Quotes</Typography>
                    <Button 
                        variant="contained" 
                        startIcon={<AddIcon />} 
                        onClick={() => navigate(`/quotes/new?client_id=${id}`)}
                        sx={{ 
                            borderRadius: 2, 
                            fontWeight: 'bold', 
                            px: 3, 
                            boxShadow: '0 4px 14px rgba(25,118,210,0.3)',
                            background: 'linear-gradient(45deg, #1976d2 0%, #42a5f5 100%)',
                            '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 6px 20px rgba(25,118,210,0.4)', background: 'linear-gradient(45deg, #1565c0 0%, #1e88e5 100%)' }
                        }}
                    >
                        New Quote
                    </Button>
                </Box>
               
                {loadingQuotes ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : quotes.length === 0 ? (
                    <Paper elevation={0} sx={{ p: 6, textAlign: 'center', border: '2px dashed #e2e8f0', borderRadius: 4, bgcolor: "#f8fafc" }}>
                        <ReceiptIcon sx={{ fontSize: 64, color: '#cbd5e1', mb: 2 }} />
                        <Typography variant="h6" color="#64748b" fontWeight="700" mb={1}>No Quotes Found</Typography>
                        <Typography variant="body2" color="#94a3b8">This client doesn't have any quotes recorded in the CRM.</Typography>
                    </Paper>
                ) : (
                    <Grid container spacing={3}>
                          {quotes.map((quote, idx) => (
                              <Grid item xs={12} sm={6} md={4} key={quote.ref_id || idx}>
                                  <Card elevation={0} sx={{ height: '100%', display: 'flex', flexDirection: 'column', border: '1px solid #f1f5f9', borderRadius: 4, transition: 'transform 0.2s', '&:hover': { transform: 'translateY(-4px)', borderColor: '#cbd5e1', boxShadow: '0 10px 25px rgba(0,0,0,0.05)' } }}>
                                      <Box sx={{ p: 2.5, bgcolor: "#f8fafc", borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                          <Typography variant="subtitle1" fontWeight="800" color="#1e293b" sx={{ wordBreak: 'break-word', mr: 2, lineHeight: 1.3 }}>
                                              {quote.project_name || 'Unnamed Quote'}
                                          </Typography>
                                          <Chip size="small" label={quote.total} sx={{ bgcolor: 'white', color: '#3b82f6', fontWeight: '800', border: '1px solid #e2e8f0', px: 1 }} />
                                      </Box>
                                      <CardContent sx={{ flexGrow: 1, py: 2.5, px: 3 }}>
                                          <Grid container spacing={2}>
                                              <Grid item xs={6}>
                                                  <Typography variant="caption" color="#94a3b8" fontWeight="600" display="block" textTransform="uppercase" letterSpacing={0.5}>Ref ID</Typography>
                                                  <Typography variant="body2" color="#334155" fontWeight="700" sx={{ mt: 0.5 }}>{quote.ref_id || '-'}</Typography>
                                              </Grid>
                                              <Grid item xs={6}>
                                                  <Typography variant="caption" color="#94a3b8" fontWeight="600" display="block" textTransform="uppercase" letterSpacing={0.5}>Start Date</Typography>
                                                  <Typography variant="body2" color="#334155" fontWeight="700" sx={{ mt: 0.5 }}>{quote.starts || '-'}</Typography>
                                              </Grid>
                                              <Grid item xs={12}>
                                                  <Divider sx={{ my: 1, borderColor: '#f1f5f9' }} />
                                              </Grid>
                                              <Grid item xs={6}>
                                                  <Typography variant="caption" color="#94a3b8" fontWeight="600" display="block" textTransform="uppercase" letterSpacing={0.5} mb={0.5}>Quote Status</Typography>
                                                  <Chip size="small" label={quote.quote_status || 'N/A'} sx={{ height: 22, fontSize: '0.7rem', fontWeight: 700, bgcolor: quote.quote_status === 'In Progress' ? '#fffbeb' : '#f1f5f9', color: quote.quote_status === 'In Progress' ? '#d97706' : '#64748b' }} />
                                              </Grid>
                                              <Grid item xs={6}>
                                                  <Typography variant="caption" color="#94a3b8" fontWeight="600" display="block" textTransform="uppercase" letterSpacing={0.5} mb={0.5}>Closed Status</Typography>
                                                  <Chip size="small" label={quote.quote_closed_status || 'N/A'} sx={{ height: 22, fontSize: '0.7rem', fontWeight: 700, bgcolor: quote.quote_closed_status === 'Won' ? '#f0fdf4' : '#f1f5f9', color: quote.quote_closed_status === 'Won' ? '#16a34a' : '#64748b' }} />
                                              </Grid>
                                              <Grid item xs={12}>
                                                  <Divider sx={{ my: 1, borderColor: '#f1f5f9' }} />
                                              </Grid>
                                              <Grid item xs={6}>
                                                  <Typography variant="caption" color="#94a3b8" fontWeight="600" display="block" textTransform="uppercase" letterSpacing={0.5}>Country</Typography>
                                                  <Typography variant="body2" color="#334155" fontWeight="500" sx={{ mt: 0.5 }}>{quote.country || '-'}</Typography>
                                              </Grid>
                                              <Grid item xs={6}>
                                                  <Typography variant="caption" color="#94a3b8" fontWeight="600" display="block" textTransform="uppercase" letterSpacing={0.5}>Division</Typography>
                                                  <Typography variant="body2" color="#334155" fontWeight="500" sx={{ mt: 0.5 }}>{quote.division || '-'}</Typography>
                                              </Grid>
                                              {quote.main_quote_ref && (
                                                <Grid item xs={12}>
                                                    <Box sx={{ mt: 1, p: 1.5, bgcolor: "#f8fafc", borderRadius: 2, border: '1px dashed #cbd5e1' }}>
                                                        <Typography variant="caption" color="#64748b" fontWeight="600" display="block">Main Quote Ref</Typography>
                                                        <Typography variant="body2" color="#475569" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>{quote.main_quote_ref}</Typography>
                                                    </Box>
                                                </Grid>
                                              )}
                                            </Grid>
                                        </CardContent>
                                        {quote.quote_closed_status === 'Won' ? (
                                            <CardActions sx={{ borderTop: '1px solid #f1f5f9', bgcolor: '#ffffff', p: 2, pt: 1.5 }}>
                                                <Button 
                                                    fullWidth
                                                    variant="contained" 
                                                    onClick={() => handleOpenCreateProject(quote)}
                                                    sx={{ 
                                                        borderRadius: 2, 
                                                        fontWeight: 'bold', 
                                                        py: 1,
                                                        boxShadow: 'none',
                                                        background: '#ebf5ff',
                                                        color: '#2563eb',
                                                        '&:hover': { background: '#2563eb', color: 'white', boxShadow: '0 4px 12px rgba(37,99,235,0.2)' }
                                                    }}
                                                >
                                                    Create Project
                                                </Button>
                                            </CardActions>
                                        ) : (
                                            <CardActions sx={{ borderTop: '1px solid #f1f5f9', bgcolor: '#f8fafc', p: 2, pt: 1.5 }}>
                                                <Button 
                                                    fullWidth
                                                    variant="contained" 
                                                    disabled
                                                    title="Quote must be Won before creating a project"
                                                    sx={{ 
                                                        borderRadius: 2, 
                                                        fontWeight: 'bold', 
                                                        py: 1,
                                                        boxShadow: 'none',
                                                        backgroundColor: '#e2e8f0',
                                                        color: '#94a3b8',
                                                        '&.Mui-disabled': { backgroundColor: '#e2e8f0', color: '#94a3b8' }
                                                    }}
                                                >
                                                    {quote.quote_closed_status === 'Lost' ? 'Quote Lost' : 'Not Won Yet'}
                                                </Button>
                                            </CardActions>
                                        )}
                                    </Card>
                              </Grid>
                            ))}
                      </Grid>
                )}
            </Box>
          </TabPanel>
        )}

        <TabPanel value={tabValue} index={6}>
            <Box sx={{ mt: 1, p: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4, flexWrap: 'wrap', gap: 2 }}>
                    <Typography variant="h5" fontWeight="800" color="#1e293b">Client Statistics</Typography>
                </Box>
               
                {loadingQuotes ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <Box>
                        {(() => {
                            const totalQuotes = quotes.length;
                            const quotesWon = quotes.filter(q => q.quote_closed_status === 'Won').length;
                            const quotesLost = quotes.filter(q => q.quote_closed_status === 'Lost').length;
                            const quotesOpen = totalQuotes - quotesWon - quotesLost;
                            const quoteWinRate = totalQuotes > 0 ? Math.round((quotesWon / totalQuotes) * 100) : 0;
                            const totalAmountWon = quotes.filter(q => q.quote_closed_status === 'Won').reduce((acc, q) => acc + (parseFloat(String(q.total || 0).replace(/[^0-9.-]+/g, '')) || 0), 0);
                            
                            const projects = client?.projects || [];
                            const totalProjects = projects.length;
                            const projectsDone = projects.filter(p => String(p.status).toLowerCase() === 'done').length;
                            const projectsInProgress = projects.filter(p => ['in progress', 'ongoing', 'active'].includes(String(p.status).toLowerCase())).length;
                            const projectsOther = totalProjects - projectsDone - projectsInProgress;
                            const totalBudget = projects.reduce((acc, p) => acc + (parseFloat(p.budget) || 0), 0);
                            const totalSpent = projects.reduce((acc, p) => acc + (parseFloat(p.spent) || 0), 0);

                            const quoteChartData = {
                                labels: ['Won', 'Lost', 'Open'],
                                datasets: [{
                                    data: [quotesWon, quotesLost, quotesOpen],
                                    backgroundColor: ['#16a34a', '#ef4444', '#f59e0b'],
                                    borderWidth: 0
                                }]
                            };

                            const projectChartData = {
                                labels: ['Done', 'In Progress', 'Other'],
                                datasets: [{
                                    data: [projectsDone, projectsInProgress, projectsOther],
                                    backgroundColor: ['#16a34a', '#3b82f6', '#cbd5e1'],
                                    borderWidth: 0
                                }]
                            };

                            const financialChartData = {
                                labels: [''],
                                datasets: [
                                    { label: 'Amount Won (€)', data: [totalAmountWon], backgroundColor: '#16a34a', borderRadius: 4 },
                                    { label: 'Budget (€)', data: [totalBudget], backgroundColor: '#3b82f6', borderRadius: 4 },
                                    { label: 'Spent (€)', data: [totalSpent], backgroundColor: '#f59e0b', borderRadius: 4 }
                                ]
                            };

                            const chartOptions = { responsive: true, maintainAspectRatio: false, plugins: { legend: { position: 'bottom' } } };
                            const barOptions = { responsive: true, maintainAspectRatio: false, plugins: { legend: { position: 'bottom' } }, scales: { y: { beginAtZero: true } } };

                            return (
                                <Grid container spacing={3}>
                                    {/* --- Row 1: Metrics --- */}
                                    <Grid item xs={12} lg={4}>
                                        <Card elevation={0} sx={{ height: '100%', border: '1px solid #f1f5f9', borderRadius: 4, bgcolor: "#f8fafc" }}>
                                            <CardContent sx={{ p: 4 }}>
                                                <Box display="flex" alignItems="center" mb={2}>
                                                    <Avatar sx={{ bgcolor: '#ebf5ff', color: '#2563eb', mr: 2, p: 2 }}><ReceiptIcon /></Avatar>
                                                    <Typography variant="h6" fontWeight="800" color="#1e293b">Quotes Pipeline</Typography>
                                                </Box>
                                                <Divider sx={{ my: 2, borderColor: '#e2e8f0' }} />
                                                <Box display="flex" justifyContent="space-between" mb={1.5}>
                                                    <Typography color="#64748b" fontWeight="600">Total Quotes</Typography>
                                                    <Chip size="small" label={totalQuotes} sx={{ fontWeight: 800, bgcolor: 'white', border: '1px solid #e2e8f0' }} />
                                                </Box>
                                                <Box display="flex" justifyContent="space-between" mb={1.5}>
                                                    <Typography color="#64748b" fontWeight="600">Win Rate</Typography>
                                                    <Typography color="#3b82f6" fontWeight="800">{quoteWinRate}%</Typography>
                                                </Box>
                                                <Box display="flex" justifyContent="space-between" mb={1.5}>
                                                    <Typography color="#64748b" fontWeight="600">Won / Lost</Typography>
                                                    <Typography color="#1e293b" fontWeight="800"><span style={{color:'#16a34a'}}>{quotesWon}</span> / <span style={{color:'#ef4444'}}>{quotesLost}</span></Typography>
                                                </Box>
                                                <Box display="flex" justifyContent="space-between">
                                                    <Typography color="#64748b" fontWeight="600">Open Quotes</Typography>
                                                    <Typography color="#f59e0b" fontWeight="800">{quotesOpen}</Typography>
                                                </Box>
                                            </CardContent>
                                        </Card>
                                    </Grid>

                                    <Grid item xs={12} lg={4}>
                                        <Card elevation={0} sx={{ height: '100%', border: '1px solid #f1f5f9', borderRadius: 4, bgcolor: "#f8fafc" }}>
                                            <CardContent sx={{ p: 4 }}>
                                                <Box display="flex" alignItems="center" mb={2}>
                                                    <Avatar sx={{ bgcolor: '#f0fdf4', color: '#16a34a', mr: 2, p: 2 }}><AccountTreeIcon /></Avatar>
                                                    <Typography variant="h6" fontWeight="800" color="#1e293b">Projects Health</Typography>
                                                </Box>
                                                <Divider sx={{ my: 2, borderColor: '#e2e8f0' }} />
                                                <Box display="flex" justifyContent="space-between" mb={1.5}>
                                                    <Typography color="#64748b" fontWeight="600">Total Projects</Typography>
                                                    <Chip size="small" label={totalProjects} sx={{ fontWeight: 800, bgcolor: 'white', border: '1px solid #e2e8f0' }} />
                                                </Box>
                                                <Box display="flex" justifyContent="space-between" mb={1.5}>
                                                    <Typography color="#64748b" fontWeight="600">Done</Typography>
                                                    <Typography color="#16a34a" fontWeight="800">{projectsDone}</Typography>
                                                </Box>
                                                <Box display="flex" justifyContent="space-between" mb={1.5}>
                                                    <Typography color="#64748b" fontWeight="600">In Progress</Typography>
                                                    <Typography color="#3b82f6" fontWeight="800">{projectsInProgress}</Typography>
                                                </Box>
                                                <Box display="flex" justifyContent="space-between">
                                                    <Typography color="#64748b" fontWeight="600">Other (Draft, Cancelled)</Typography>
                                                    <Typography color="#94a3b8" fontWeight="800">{projectsOther}</Typography>
                                                </Box>
                                            </CardContent>
                                        </Card>
                                    </Grid>

                                    <Grid item xs={12} lg={4}>
                                        <Card elevation={0} sx={{ height: '100%', border: '1px solid #f1f5f9', borderRadius: 4, bgcolor: "#f8fafc" }}>
                                            <CardContent sx={{ p: 4 }}>
                                                <Box display="flex" alignItems="center" mb={2}>
                                                    <Avatar sx={{ bgcolor: '#fef3c7', color: '#d97706', mr: 2, p: 2 }}><AttachMoneyIcon /></Avatar>
                                                    <Typography variant="h6" fontWeight="800" color="#1e293b">Financial Overview</Typography>
                                                </Box>
                                                <Divider sx={{ my: 2, borderColor: '#e2e8f0' }} />
                                                <Box display="flex" justifyContent="space-between" mb={1.5}>
                                                    <Typography color="#64748b" fontWeight="600">Amount Won</Typography>
                                                    <Typography color="#16a34a" fontWeight="800">
                                                        € {totalAmountWon.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                    </Typography>
                                                </Box>
                                                <Box display="flex" justifyContent="space-between" mb={1.5}>
                                                    <Typography color="#64748b" fontWeight="600">Total Budget</Typography>
                                                    <Typography color="#3b82f6" fontWeight="800">
                                                        € {totalBudget.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                    </Typography>
                                                </Box>
                                                <Box display="flex" justifyContent="space-between">
                                                    <Typography color="#64748b" fontWeight="600">Total Spent</Typography>
                                                    <Typography color="#f59e0b" fontWeight="800">
                                                        € {totalSpent.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                    </Typography>
                                                </Box>
                                            </CardContent>
                                        </Card>
                                    </Grid>

                                    {/* --- Row 2: Charts --- */}
                                    <Grid item xs={12} lg={4}>
                                        <Card elevation={0} sx={{ height: '100%', border: '1px solid #f1f5f9', borderRadius: 4, bgcolor: '#ffffff' }}>
                                            <CardContent>
                                                <Typography variant="subtitle1" fontWeight="800" color="#1e293b" mb={2} align="center">Quotes Distribution</Typography>
                                                <Box sx={{ height: 260, display: 'flex', justifyContent: 'center' }}>
                                                    {totalQuotes > 0 ? <Pie data={quoteChartData} options={chartOptions} /> : <Typography color="#94a3b8" variant="body2" sx={{ mt: 10 }}>No quotes data</Typography>}
                                                </Box>
                                            </CardContent>
                                        </Card>
                                    </Grid>

                                    <Grid item xs={12} lg={4}>
                                        <Card elevation={0} sx={{ height: '100%', border: '1px solid #f1f5f9', borderRadius: 4, bgcolor: '#ffffff' }}>
                                            <CardContent>
                                                <Typography variant="subtitle1" fontWeight="800" color="#1e293b" mb={2} align="center">Projects Distribution</Typography>
                                                <Box sx={{ height: 260, display: 'flex', justifyContent: 'center' }}>
                                                    {totalProjects > 0 ? <Pie data={projectChartData} options={chartOptions} /> : <Typography color="#94a3b8" variant="body2" sx={{ mt: 10 }}>No projects data</Typography>}
                                                </Box>
                                            </CardContent>
                                        </Card>
                                    </Grid>

                                    <Grid item xs={12} lg={4}>
                                        <Card elevation={0} sx={{ height: '100%', border: '1px solid #f1f5f9', borderRadius: 4, bgcolor: '#ffffff' }}>
                                            <CardContent>
                                                <Typography variant="subtitle1" fontWeight="800" color="#1e293b" mb={2} align="center">Financial Overview</Typography>
                                                <Box sx={{ height: 260, display: 'flex', justifyContent: 'center', mt: 2 }}>
                                                    {(totalBudget > 0 || totalAmountWon > 0 || totalSpent > 0) ? <Bar data={financialChartData} options={barOptions} /> : <Typography color="#94a3b8" variant="body2" sx={{ mt: 10 }}>No financial data</Typography>}
                                                </Box>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                </Grid>
                            );
                        })()}
                    </Box>
                )}
            </Box>
        </TabPanel>
        
        {editMode && (
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 3, mb: 2, px: 3, pb: 2 }}>
              {['Admin', 'Manager'].includes(userRole) && client.projects && client.projects.length === 0 && (
                  <Button
                      variant="outlined"
                      startIcon={<DeleteIcon />}
                      onClick={handleDeleteClient}
                      sx={{
                          borderColor: '#f44336', color: '#f44336',
                          '&:hover': { borderColor: '#d32f2f', bgcolor: 'rgba(244,67,54,0.1)' },
                          borderRadius: 2, fontWeight: 'bold', px: 3
                      }}
                  >
                      Delete Client
                  </Button>
              )}
              <Button
                  variant="contained"
                  startIcon={updateLoading ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
                  onClick={handleUpdateClient}
                  disabled={updateLoading}
                  sx={{
                      borderRadius: 2,
                      bgcolor: '#4caf50',
                      color: 'white',
                      fontWeight: 'bold',
                      px: 4,
                      minWidth: 160,
                      boxShadow: '0 4px 14px rgba(76,175,80,0.4)',
                      '&:hover': {
                          bgcolor: '#388e3c',
                          transform: 'translateY(-2px)'
                      },
                      transition: 'all 0.2s'
                  }}
              >
                  {updateLoading ? 'Saving...' : 'Save Changes'}
              </Button>
          </Box>
        )}
      </Paper>

      {/* Preview Dialog */}
      <Dialog 
        open={Boolean(previewFile)} 
        onClose={() => setPreviewFile(null)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { minHeight: '80vh' } }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" component="div" noWrap sx={{ maxWidth: '80%' }}>
                Preview: {previewFile?.filename}
            </Typography>
            <IconButton onClick={() => setPreviewFile(null)} size="small">
                <CloseIcon />
            </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0, bgcolor: '#f5f5f5', display: 'flex', justifyContent: 'center', alignItems: 'center', position: 'relative' }}>
            {previewFile && previewLoading && previewFile.filename.match(/\.(jpeg|jpg|gif|png|webp|pdf)$/i) && (
                <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 1 }}>
                    <CircularProgress />
                </Box>
            )}
            {previewFile && (
                previewFile.filename.match(/\.(jpeg|jpg|gif|png|webp)$/i) ? (
                    <Box component="img" src={previewFile.url} sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} alt="Preview" onLoad={() => setPreviewLoading(false)} onError={() => setPreviewLoading(false)} />
                ) : previewFile.filename.match(/\.pdf$/i) ? (
                    <iframe src={previewFile.url} width="100%" height="100%" style={{ border: 'none', backgroundColor: 'transparent' }} title="PDF Preview" onLoad={() => setPreviewLoading(false)} />
                ) : (
                    <Box sx={{ textAlign: 'center', p: 4 }}>
                        <FilePresentIcon sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
                        <Typography variant="h6" color="text.secondary">Preview not available for this file type.</Typography>
                        <Button variant="contained" sx={{ mt: 3 }} onClick={() => { handleDownloadDocument(previewFile.filename); setPreviewFile(null); }}>
                            Download File Instead
                        </Button>
                    </Box>
                )
            )}
        </DialogContent>
      </Dialog>

      {/* Add Contact Dialog */}
      <Dialog open={openContactDialog} onClose={() => setOpenContactDialog(false)} PaperProps={{ sx: { borderRadius: 4, width: '100%', maxWidth: 600 } }}>
        <DialogTitle sx={{ fontWeight: 800, pb: 1 }}>Add Contact to {client?.name}</DialogTitle>
        <DialogContent dividers sx={{ borderColor: '#f1f5f9', py: 3 }}>
            {client?.crmID && (
                <TextField 
                    label="Account Name (Dynamics 365)" 
                    fullWidth 
                    margin="dense"
                    value={client.name} 
                    disabled
                    sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: "#f8fafc" } }}
                />
            )}
            <Grid container spacing={2}>
                <Grid item xs={12} sm={3}>
                    <FormControl fullWidth margin="dense" sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}>
                        <InputLabel>Sal*</InputLabel>
                        <Select
                            label="Sal*"
                            value={newContact.salutation}
                            onChange={(e) => setNewContact({...newContact, salutation: e.target.value})}
                        >
                            <MenuItem value="Mr.">Mr.</MenuItem>
                            <MenuItem value="Mrs.">Mrs.</MenuItem>
                            <MenuItem value="Miss">Miss</MenuItem>
                            <MenuItem value="Dr.">Dr.</MenuItem>
                            <MenuItem value="Prof.">Prof.</MenuItem>
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={12} sm={4.5}>
                    <TextField 
                        label="First Name" 
                        fullWidth 
                        required 
                        margin="dense"
                        value={newContact.name} 
                        onChange={(e) => setNewContact({...newContact, name: e.target.value})} 
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                </Grid>
                <Grid item xs={12} sm={4.5}>
                    <TextField 
                        label="Last Name" 
                        fullWidth 
                        required 
                        margin="dense"
                        value={newContact.last_name} 
                        onChange={(e) => setNewContact({...newContact, last_name: e.target.value})} 
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                </Grid>
            </Grid>
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
                <Grid item xs={12} sm={6}>
                    <FormControl fullWidth margin="dense" sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}>
                        <InputLabel>Department{client?.crmID ? ' *' : ''}</InputLabel>
                        <Select
                            label={`Department${client?.crmID ? ' *' : ''}`}
                            value={newContact.department || ''}
                            onChange={(e) => setNewContact({...newContact, department: e.target.value})}
                        >
                            <MenuItem value={100000000}>Human Resource</MenuItem>
                            <MenuItem value={100000001}>Marketing</MenuItem>
                            <MenuItem value={100000002}>Customer Service</MenuItem>
                            <MenuItem value={100000003}>Sales</MenuItem>
                            <MenuItem value={100000004}>Accounting and Finance</MenuItem>
                            <MenuItem value={100000005}>Distribution</MenuItem>
                            <MenuItem value={100000006}>Research and Development</MenuItem>
                            <MenuItem value={100000007}>Administrative/Management</MenuItem>
                            <MenuItem value={100000008}>Production</MenuItem>
                            <MenuItem value={100000009}>Operations</MenuItem>
                            <MenuItem value={100000010}>IT</MenuItem>
                            <MenuItem value={100000011}>Procurement</MenuItem>
                            <MenuItem value={100000012}>Legal</MenuItem>
                            <MenuItem value={100000013}>Other</MenuItem>
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                    <FormControl fullWidth margin="dense" sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}>
                        <InputLabel>Profession Level{client?.crmID ? ' *' : ''}</InputLabel>
                        <Select
                            label={`Profession Level${client?.crmID ? ' *' : ''}`}
                            value={newContact.profession_level || ''}
                            onChange={(e) => setNewContact({...newContact, profession_level: e.target.value})}
                        >
                            <MenuItem value={100000000}>Student</MenuItem>
                            <MenuItem value={100000001}>Junior</MenuItem>
                            <MenuItem value={100000002}>Senior</MenuItem>
                            <MenuItem value={100000003}>Manager</MenuItem>
                            <MenuItem value={100000004}>Director</MenuItem>
                            <MenuItem value={100000005}>CEO</MenuItem>
                            <MenuItem value={100000006}>Chairman</MenuItem>
                            <MenuItem value={100000007}>Owner</MenuItem>
                            <MenuItem value={100000008}>Other</MenuItem>
                        </Select>
                    </FormControl>
                </Grid>
            </Grid>
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
                <Grid item xs={12} sm={6}>
                    <TextField 
                        label="Job Title" 
                        fullWidth 
                        margin="dense"
                        value={newContact.job_title} 
                        onChange={(e) => setNewContact({...newContact, job_title: e.target.value})} 
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <FormControl fullWidth margin="dense" sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}>
                        <InputLabel>Decision Maker{client?.crmID ? ' *' : ''}</InputLabel>
                        <Select
                            label={`Decision Maker${client?.crmID ? ' *' : ''}`}
                            value={newContact.decision_maker || false}
                            onChange={(e) => setNewContact({...newContact, decision_maker: e.target.value})}
                        >
                            <MenuItem value={true}>Yes</MenuItem>
                            <MenuItem value={false}>No</MenuItem>
                        </Select>
                    </FormControl>
                </Grid>
            </Grid>
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
                <Grid item xs={12} sm={6}>
                    <TextField 
                        label="Email" 
                        type="email"
                        fullWidth 
                        margin="dense"
                        value={newContact.email} 
                        onChange={(e) => setNewContact({...newContact, email: e.target.value})} 
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <TextField 
                        label="Phone" 
                        fullWidth 
                        margin="dense"
                        value={newContact.phone} 
                        onChange={(e) => setNewContact({...newContact, phone: e.target.value})} 
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                </Grid>
            </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 2, borderTop: '1px solid #f1f5f9' }}>
            <Button onClick={() => setOpenContactDialog(false)} sx={{ borderRadius: 2, fontWeight: 'bold', color: '#64748b' }}>Cancel</Button>
            <Button onClick={handleAddContact} variant="contained" disabled={loadingContacts || !newContact.name} sx={{ borderRadius: 2, fontWeight: 'bold', boxShadow: 'none', background: 'linear-gradient(45deg, #1976d2 0%, #42a5f5 100%)', '&:hover': { boxShadow: '0 4px 12px rgba(25,118,210,0.3)' } }}>
                {loadingContacts ? <CircularProgress size={24} color="inherit" /> : 'Save Contact'}
            </Button>
        </DialogActions>
      </Dialog>

      {/* Follow-up Dialog */}
      <Dialog open={followupDialogOpen} onClose={() => setFollowupDialogOpen(false)} PaperProps={{ sx: { borderRadius: 4, width: '100%', maxWidth: 700, overflow: 'hidden', boxShadow: '0 20px 40px rgba(0,0,0,0.1)' } }}>
          <Box sx={{ background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)', p: 3, display: 'flex', alignItems: 'center', gap: 2, color: 'white' }}>
              <EventNoteIcon sx={{ fontSize: 32, opacity: 0.9 }} />
              <Box>
                  <Typography variant="h5" sx={{ fontWeight: 800, letterSpacing: 0.5 ,color: 'white' }}>
                      {editingFollowupId ? "Edit Follow-Up" : "Log Follow-Up"}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.8, mt: 0.5 }}>
                      {editingFollowupId ? "Update details for this scheduled interaction." : "Record a new interaction or log an upcoming event."}
                  </Typography>
              </Box>
          </Box>
          <DialogContent sx={{ p: { xs: 2, md: 4 }, bgcolor: '#f8fafc', display: 'flex', flexDirection: 'column', gap: 4 }}>
              <form id="followup-form" onSubmit={handleFollowupSubmit}>
                  
                  {/* Core Info */}
                  <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid #e2e8f0', bgcolor: 'white', mb: 4 }}>
                      <Typography variant="subtitle2" color="#64748b" fontWeight="800" textTransform="uppercase" mb={3}>Interaction Details</Typography>
                      <Grid container spacing={3}>
                          <Grid item xs={12}>
                              <TextField fullWidth label="Details" multiline rows={3} value={newFollowup.details || ''} onChange={(e) => setNewFollowup({ ...newFollowup, details: e.target.value })} required sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
                          </Grid>
                          <Grid item xs={12} md={6}>
                              <Autocomplete options={users || []} getOptionLabel={(option) => option.full_name || option.username} value={(users || []).find(u => u.id === newFollowup.assigned_to_id) || null} onChange={(event, newValue) => { setNewFollowup({ ...newFollowup, assigned_to_id: newValue ? newValue.id : '' }); }} renderInput={(params) => ( <TextField {...params} label="Assigned User" required sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} /> )} />
                          </Grid>
                          <Grid item xs={12} md={3}>
                                <FormControl fullWidth sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}>
                                    <InputLabel>Project (Opt)</InputLabel>
                                    <Select value={newFollowup.project_id || ''} onChange={(e) => setNewFollowup({ ...newFollowup, project_id: e.target.value })}>
                                        <MenuItem value=""><em>None</em></MenuItem>
                                        {client && client.projects && client.projects.map(p => ( <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem> ))}
                                    </Select>
                                </FormControl>
                          </Grid>
                          <Grid item xs={12} md={3}>
                              <FormControl fullWidth sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}>
                                  <InputLabel>CRM Lead (Opt)</InputLabel>
                                  <Select value={newFollowup.lead_crm_id || ''} onChange={(e) => setNewFollowup({ ...newFollowup, lead_crm_id: e.target.value })}>
                                      <MenuItem value=""><em>None</em></MenuItem>
                                      {leads && leads.map(l => ( <MenuItem key={l.leadid} value={l.leadid}>{l.companyname || l.fullname || l.subject}</MenuItem> ))}
                                  </Select>
                              </FormControl>
                          </Grid>
                      </Grid>
                  </Paper>

                  {/* Current Interaction Schedule */}
                  <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid #e2e8f0', bgcolor: 'white', mb: 4 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                          <DateRangeIcon sx={{ color: '#3b82f6' }} />
                          <Typography variant="subtitle2" color="#1e293b" fontWeight="800" textTransform="uppercase">Current Interaction Timing</Typography>
                      </Box>
                      <Grid container spacing={3}>
                          <Grid item xs={12} sm={3}>
                              <TextField fullWidth label="Start Date" type="date" InputLabelProps={{ shrink: true }} value={newFollowup.startDate} onChange={(e) => setNewFollowup({ ...newFollowup, startDate: e.target.value })} required sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
                          </Grid>
                          <Grid item xs={12} sm={3}>
                              <TextField fullWidth label="Start Time" type="time" InputLabelProps={{ shrink: true }} value={newFollowup.startTime} onChange={(e) => setNewFollowup({ ...newFollowup, startTime: e.target.value })} required sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
                          </Grid>
                          <Grid item xs={12} sm={3}>
                              <TextField fullWidth label="End Date" type="date" InputLabelProps={{ shrink: true }} value={newFollowup.endDate} onChange={(e) => setNewFollowup({ ...newFollowup, endDate: e.target.value })} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
                          </Grid>
                          <Grid item xs={12} sm={3}>
                              <TextField fullWidth label="End Time" type="time" InputLabelProps={{ shrink: true }} value={newFollowup.endTime} onChange={(e) => setNewFollowup({ ...newFollowup, endTime: e.target.value })} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
                          </Grid>
                      </Grid>
                  </Paper>

                  {/* Next Visit Schedule */}
                  <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid #ede9fe', bgcolor: '#f5f3ff' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                          <AccessTimeIcon sx={{ color: '#7c3aed' }} />
                          <Typography variant="subtitle2" color="#5b21b6" fontWeight="800" textTransform="uppercase">Plan Next Visit</Typography>
                      </Box>
                      <Grid container spacing={3}>
                          <Grid item xs={12} sm={3}>
                              <TextField fullWidth label="Next Visit Start Date" type="date" InputLabelProps={{ shrink: true }} value={newFollowup.nextVisitStartDate} onChange={(e) => setNewFollowup({ ...newFollowup, nextVisitStartDate: e.target.value })} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: 'white' } }} />
                          </Grid>
                          <Grid item xs={12} sm={3}>
                              <TextField fullWidth label="Next Visit Start Time" type="time" InputLabelProps={{ shrink: true }} value={newFollowup.nextVisitStartTime} onChange={(e) => setNewFollowup({ ...newFollowup, nextVisitStartTime: e.target.value })} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: 'white' } }} />
                          </Grid>
                          <Grid item xs={12} sm={3}>
                              <TextField fullWidth label="Next Visit End Date" type="date" InputLabelProps={{ shrink: true }} value={newFollowup.nextVisitEndDate} onChange={(e) => setNewFollowup({ ...newFollowup, nextVisitEndDate: e.target.value })} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: 'white' } }} />
                          </Grid>
                          <Grid item xs={12} sm={3}>
                              <TextField fullWidth label="Next Visit End Time" type="time" InputLabelProps={{ shrink: true }} value={newFollowup.nextVisitEndTime} onChange={(e) => setNewFollowup({ ...newFollowup, nextVisitEndTime: e.target.value })} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: 'white' } }} />
                          </Grid>
                      </Grid>
                      <Typography variant="caption" sx={{ display: 'block', mt: 2, color: '#6d28d9', fontStyle: 'italic', fontWeight: 500 }}>* Automatically creates a dedicated follow-up for the next visit dates provided.</Typography>
                  </Paper>
                  
                  {/* Invisible Submit Button */}
                  <button type="submit" style={{ display: 'none' }}></button>
              </form>
          </DialogContent>
          <DialogActions sx={{ px: 4, py: 3, borderTop: '1px solid #f1f5f9', bgcolor: 'white' }}>
              <Button onClick={() => setFollowupDialogOpen(false)} sx={{ borderRadius: 2, fontWeight: 'bold', color: '#64748b', px: 3 }}>
                  Cancel
              </Button>
              <Button onClick={handleFollowupSubmit} variant="contained" disabled={followupLoading || (!newFollowup.details)} sx={{ borderRadius: 2, fontWeight: 'bold', px: 4, py: 1, boxShadow: 'none', background: 'linear-gradient(45deg, #1976d2 0%, #42a5f5 100%)', '&:hover': { boxShadow: '0 4px 12px rgba(25,118,210,0.3)' } }}>
                  {followupLoading ? <CircularProgress size={24} color="inherit" /> : (editingFollowupId ? "Update" : "Save Follow-Up")}
              </Button>
          </DialogActions>
      </Dialog>
    
        {/* Create Project from Quote Dialog */}
        <Dialog open={openCreateProject} onClose={() => setOpenCreateProject(false)} PaperProps={{ sx: { borderRadius: 4, width: '100%', maxWidth: 600 } }}>
            <DialogTitle sx={{ fontWeight: 800, pb: 1 }}>{createProjectData.quote_ref === 'N/A' || !createProjectData.quote_ref ? 'Create New Project' : 'Create Project from Quote'}</DialogTitle>
            <DialogContent dividers sx={{ borderColor: '#f1f5f9', py: 3 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                    <TextField label="Name" fullWidth required value={createProjectData.name} onChange={e => setCreateProjectData({...createProjectData, name: e.target.value})} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
                    <TextField label="Description" fullWidth multiline rows={3} value={createProjectData.description} onChange={e => setCreateProjectData({...createProjectData, description: e.target.value})} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
                    <TextField label="Quote Ref (or N/A)" fullWidth required value={createProjectData.quote_ref} onChange={e => setCreateProjectData({...createProjectData, quote_ref: e.target.value})} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
                    
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <TextField label="Start Date" type="date" fullWidth InputLabelProps={{ shrink: true }} value={createProjectData.start_date} onChange={e => setCreateProjectData({...createProjectData, start_date: e.target.value})} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
                        <TextField label="Due Date" type="date" fullWidth InputLabelProps={{ shrink: true }} value={createProjectData.due_date} onChange={e => setCreateProjectData({...createProjectData, due_date: e.target.value})} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
                    </Box>

                    <FormControl fullWidth sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}>
                        <InputLabel>Priority</InputLabel>
                        <Select value={createProjectData.priority} label="Priority" onChange={e => setCreateProjectData({...createProjectData, priority: e.target.value})}>
                            <MenuItem value="High">High</MenuItem>
                            <MenuItem value="Medium">Medium</MenuItem>
                            <MenuItem value="Low">Low</MenuItem>
                        </Select>
                    </FormControl>

                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <FormControl fullWidth required sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}>
                            <InputLabel>Country</InputLabel>
                            <Select value={createProjectData.country_id} label="Country" onChange={e => setCreateProjectData({...createProjectData, country_id: e.target.value})}>
                                {countries.map(c => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
                            </Select>
                        </FormControl>
                        <FormControl fullWidth required sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}>
                            <InputLabel>Division</InputLabel>
                            <Select value={createProjectData.division_id} label="Division" onChange={e => setCreateProjectData({...createProjectData, division_id: e.target.value})}>
                                {divisions.map(d => <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>)}
                            </Select>
                        </FormControl>
                    </Box>
                </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 3, pt: 2, borderTop: '1px solid #f1f5f9' }}>
                <Button onClick={() => setOpenCreateProject(false)} sx={{ borderRadius: 2, fontWeight: 'bold', color: '#64748b' }}>Cancel</Button>
                <Button variant="contained" onClick={handleCreateProject} disabled={creatingProject} sx={{ borderRadius: 2, fontWeight: 'bold', boxShadow: 'none', background: 'linear-gradient(45deg, #1976d2 0%, #42a5f5 100%)', '&:hover': { boxShadow: '0 4px 12px rgba(25,118,210,0.3)' } }}>
                    {creatingProject ? <CircularProgress size={24} color="inherit" /> : 'Create Project'}
                </Button>
            </DialogActions>
        </Dialog>

        {/* Action Confirmation Dialog */}
        <Dialog open={confirmAction.open} onClose={() => !confirmAction.loading && setConfirmAction({ open: false, type: null, loading: false, rejectReason: '' })} PaperProps={{ sx: { borderRadius: 4, width: '100%', maxWidth: 500 } }}>
            <DialogTitle sx={{ fontWeight: 800, pb: 1, color: confirmAction.type === 'approve' ? '#16a34a' : '#dc2626' }}>
                {confirmAction.type === 'approve' ? 'Approve Client' : 'Reject Client'}
            </DialogTitle>
            <DialogContent dividers sx={{ borderColor: '#f1f5f9', py: 3 }}>
                <Box sx={{ py: 1 }}>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                        Are you sure you want to {confirmAction.type} the client <strong>{client?.name}</strong>?
                    </Typography>
                    {confirmAction.type === 'reject' && (
                        <TextField
                            fullWidth
                            label="Rejection Reason"
                            multiline
                            rows={3}
                            value={confirmAction.rejectReason}
                            onChange={(e) => setConfirmAction({ ...confirmAction, rejectReason: e.target.value })}
                            placeholder="Please provide a reason for rejecting this client..."
                            required
                            disabled={confirmAction.loading}
                        />
                    )}
                </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 3, pt: 2, borderTop: '1px solid #f1f5f9' }}>
                <Button onClick={() => setConfirmAction({ open: false, type: null, loading: false, rejectReason: '' })} disabled={confirmAction.loading} sx={{ borderRadius: 2, fontWeight: 'bold', color: '#64748b' }}>
                    Cancel
                </Button>
                <Button 
                    variant="contained" 
                    color={confirmAction.type === 'approve' ? 'success' : 'error'} 
                    onClick={handleConfirmSubmit} 
                    disabled={confirmAction.loading || (confirmAction.type === 'reject' && !confirmAction.rejectReason.trim())}
                    startIcon={confirmAction.loading ? <CircularProgress size={18} color="inherit" /> : null}
                    sx={{ borderRadius: 2, fontWeight: 'bold', boxShadow: 'none', minWidth: 160, '&:hover': { boxShadow: '0 4px 12px rgba(0,0,0,0.1)' } }}
                >
                    {confirmAction.loading ? 'Processing...' : (confirmAction.type === 'approve' ? 'Confirm Approval' : 'Confirm Rejection')}
                </Button>
            </DialogActions>
        </Dialog>
    </Box>

  );
};

export default ClientDetails;
